import { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react';
import {
  Select, Button, Modal, Form, Input, Radio, Space, Card, Drawer, Tree,
  message, Tag, Typography, Empty, Divider, Popconfirm, Spin, Tooltip,
  Progress, Badge, List, Popover, Checkbox, Segmented,
} from 'antd';
import {
  PlusOutlined, ReloadOutlined, DeleteOutlined, EditOutlined, SendOutlined,
  CheckCircleOutlined, CheckCircleFilled, CloseCircleOutlined, CloseOutlined,
  DesktopOutlined, CloudServerOutlined,
  FolderOpenOutlined, CheckOutlined, FileTextOutlined, CodeOutlined,
  BugOutlined, FilePdfOutlined, LoadingOutlined,
  HistoryOutlined, CopyOutlined, RedoOutlined, ClearOutlined,
  ClockCircleOutlined, ThunderboltOutlined, BranchesOutlined,
  DownOutlined, RightOutlined, FolderOutlined, FileOutlined,
  UploadOutlined, InboxOutlined, EyeOutlined, SearchOutlined,
  RocketOutlined, UnorderedListOutlined, PaperClipOutlined,
  FileWordOutlined, FileExcelOutlined, LinkOutlined,
  PushpinOutlined, PushpinFilled, LockOutlined, UnlockOutlined, FileSearchOutlined,
} from '@ant-design/icons';
import { io, Socket } from 'socket.io-client';
import { useAuthStore } from '../../stores/authStore';
import { useWorkspaceStore } from '../../stores/workspaceStore';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import api from '../../services/api';
import { uploadCollabFile } from '../../services/teamCollabApi';
import {
  AgentFactoryAutoTestInput,
  AgentFactoryOptions,
  AgentFactoryProjectStatus,
  getAgentFactoryOptions,
  getAgentFactoryProjectStatus,
} from '../../services/agentFactoryApi';
import VoiceInputButton from '../../components/VoiceInputButton';
import AgentFactoryWorkspace from './AgentFactoryWorkspace';
import './dev-workspace.css';

const { TextArea } = Input;
const { Title, Text, Paragraph } = Typography;

interface Workbench { id: string; name: string; type: 'local' | 'ssh'; workPath: string; host?: string; port?: number; username?: string; status: string; hasPassword: boolean; lastTestedAt?: string; createdAt: string; projectId?: string; }
interface Project {
  id: string;
  name: string;
  description?: string;
  status: string;
  workspacePath?: string;
  settings?: Record<string, any>;
}
interface ProjectPromptUser { id: string; name: string; username: string; avatarUrl?: string; count?: number; isMine?: boolean; }
interface ProjectPrompt { id: string; sessionId: string; content: string; mode: 'design' | 'development'; status: string; kind: 'task' | 'follow_up'; createdAt: string; user: ProjectPromptUser; isMine: boolean; }
interface ProjectPromptStats { total: number; mine: number; users: number; design: number; development: number; }
type DevMode = 'design' | 'development' | 'testing' | 'deployment';
const EMPTY_PROJECT_PROMPT_STATS: ProjectPromptStats = { total: 0, mine: 0, users: 0, design: 0, development: 0 };
const PROJECT_PROMPT_PAGE_SIZE = 15;

// ===== 性能优化：memo消息组件，避免父组件重渲染时全部重新解析Markdown =====
const RISKY_HTML_PATTERN = /<\s*(?:!doctype|html|head|body|style|script|link|meta|iframe|object|embed|svg|canvas|form|input|button|select|textarea)\b|position\s*:\s*(?:fixed|absolute)|@keyframes|animation\s*:/i;
const isGeneratedStatusHtml = (content: string) => (
  content.trimStart().startsWith('<div') &&
  /(?:知识库匹配到|经验知识库匹配到|知识库搜索完成)/.test(content)
);
const renderMarkdownMessage = (content: any, className = 'dw-msg-content md-content') => (
  <div className={className} style={{fontSize:14}}>
    <ReactMarkdown remarkPlugins={[remarkGfm]}>{String(content || '')}</ReactMarkdown>
  </div>
);
const ChatMessage = memo(({ m }: { m: any }) => {
  const content = String(m.content || '');
  // 只允许当前运行时生成的轻量状态 HTML；历史里的完整页面源码/样式必须按文本渲染，避免污染 DevForge 页面。
  const canRenderInlineHtml = m.role === 'assistant' && isGeneratedStatusHtml(content) && !RISKY_HTML_PATTERN.test(content);
  return (
    <div className={`dw-msg ${m.role}`}>
      <div className="dw-msg-avatar">{m.role==='user'?'👤':m.role==='tool'?'🔧':'🤖'}</div>
      {canRenderInlineHtml
        ? <div className="dw-msg-content" dangerouslySetInnerHTML={{__html:content.replace(/\n/g,'<br/>')}}/>
        : renderMarkdownMessage(content, m.role==='tool'?'dw-msg-tool md-content':'dw-msg-content md-content')
      }
    </div>
  );
});
ChatMessage.displayName = 'ChatMessage';
const MODES: { key: DevMode; icon: any; label: string; desc: string; color: string }[] = [
  { key: 'design', icon: <FileTextOutlined />, label: '设计', desc: '需求梳理、原型设计、架构规划', color: '#1677ff' },
  { key: 'development', icon: <CodeOutlined />, label: '开发', desc: '前后端代码、接口实现', color: '#0ea5e9' },
];
function formatElapsed(ms: number) { if (!ms) return ''; if (ms < 1000) return `${ms}ms`; if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`; const mins = Math.floor(ms / 60000); const secs = ((ms % 60000) / 1000).toFixed(0).padStart(2, '0'); return `${mins}m${secs}s`; }

export default function DevWorkspace() {
  const ws = useWorkspaceStore();
  const isAdmin = useAuthStore(s => s.user?.roles?.includes('admin') || s.user?.username === 'admin');
  const [enterpriseModalOpen, setEnterpriseModalOpen] = useState(false);
  // 关键状态直接从store读写，切路由不丢失
  const messages = ws.messages;
  const todos = ws.todos;
  const totalTokens = ws.totalTokens;
  const totalTurns = ws.totalTurns;
  const elapsedMs = ws.elapsedMs;
  const agentRunning = ws.agentRunning;
  const thinkingPhase = ws.thinkingPhase;
  const sessionId = ws.sessionId;
  const chatInput = ws.chatInput;
  // 本地输入状态，避免每次按键触发全组件渲染
  const [localInput, setLocalInput] = useState(ws.chatInput);
  // 同步store→本地 和 本地→store
  useEffect(() => { setLocalInput(ws.chatInput); }, [ws.chatInput]);
  const devMode = ws.devMode as DevMode;
  const llmModel = ws.llmModel;
  const llmStatus = ws.llmStatus;
  const interruptedSession = ws.interruptedSession;
  const needConfirm = ws.needConfirm;

  // 只保留UI局部状态（切路由丢了也无所谓）
  const [workbenches, setWorkbenches] = useState<Workbench[]>([]);
  const [selectedWbId, setSelectedWbId] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectSwitching, setProjectSwitching] = useState(false);
  const projectLoadSeqRef = useRef(0);
  const [modalOpen, setModalOpen] = useState(false); const [editingWb, setEditingWb] = useState<Workbench | null>(null); const [testingId, setTestingId] = useState<string | null>(null);
  const [sending, setSending] = useState(false); const [savingWb, setSavingWb] = useState(false); const [syncing, setSyncing] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false); const [historySessions, setHistorySessions] = useState<any[]>([]); const [historyLoading, setHistoryLoading] = useState(false);
  // 任务队列
  const [queueOpen, setQueueOpen] = useState(false); const [taskQueue, setTaskQueue] = useState<any[]>([]); const [queueMessage, setQueueMessage] = useState('');
  const [showCodeTree, setShowCodeTree] = useState(false); const [fileTree, setFileTree] = useState<any[]>([]); const [treeLoading, setTreeLoading] = useState(false);
  const [stuckDetected, setStuckDetected] = useState(false);
  const [streamingContent, setStreamingContent] = useState(''); // 流式输出的实时内容
  const [enableThinking, setEnableThinking] = useState(true); const [selectedModelId, setSelectedModelId] = useState<string | undefined>(() => { try { return localStorage.getItem('dev-ws-model') || undefined; } catch { return undefined; } }); const [models, setModels] = useState<{ id: string; model: string; provider: string; apiUrl: string }[]>([]); const [lastTurnTime, setLastTurnTime] = useState(0); const [lastTurn, setLastTurn] = useState(0);
  const [form] = Form.useForm(); const [queueForm] = Form.useForm(); const [wbType, setWbType] = useState<'local' | 'ssh'>('local');
  const chatEndRef = useRef<HTMLDivElement>(null); const chatScrollRef = useRef<HTMLDivElement>(null); const socketRef = useRef<Socket | null>(null); const sessionIdRef = useRef<string | null>(ws.sessionId);
  const historySkipRef = useRef(0); const loadingOlderHistoryRef = useRef(false); const hasOlderHistoryRef = useRef(false); const prependingHistoryRef = useRef(false); const autoHistoryFillKeyRef = useRef('');
  // 设计模式选项
  const [kbDepth, setKbDepth] = useState(20);
  const [useKb, setUseKb] = useState(true);
  const [designOutput, setDesignOutput] = useState('text');
  const [webStyle, setWebStyle] = useState('none');
  const [styleOptions, setStyleOptions] = useState<{label:string;value:string}[]>([]);
  const [styleGalleryOpen, setStyleGalleryOpen] = useState(false);
  // 技术组件
  const [userComps, setUserComps] = useState<any[]>([]); const [selectedCompIds, setSelectedCompIds] = useState<string[]>([]); const [compPopOpen, setCompPopOpen] = useState(false);
  const chatTextAreaRef = useRef<any>(null);
  const [styleDetail, setStyleDetail] = useState<any[]>([]); // full style data for gallery
  const [fullPreviewStyle, setFullPreviewStyle] = useState<string | null>(null); // fullscreen preview
  const [todoListOpen, setTodoListOpen] = useState(false);
  const [newTaskStarted, setNewTaskStarted] = useState(false);
  // 文件上传
  const [attachedFile, setAttachedFile] = useState<{ name: string; content: string; uploading: boolean; error?: string } | null>(null);
  // 项目记忆面板
  const [memoryPanelOpen, setMemoryPanelOpen] = useState(false);
  const [memories, setMemories] = useState<any[]>([]);
  const [memoryEdit, setMemoryEdit] = useState<any>(null); // {id,title,content,type} or null for new
  const [memoryExtracting, setMemoryExtracting] = useState(false);
  // 接口文档灌输
  const [apiDocOpen, setApiDocOpen] = useState(false);
  const [apiDocName, setApiDocName] = useState('');
  const [apiDocUrl, setApiDocUrl] = useState('');
  const [apiDocFile, setApiDocFile] = useState<File | null>(null);
  const [apiDocLoading, setApiDocLoading] = useState(false);
  const apiDocFileRef = useRef<HTMLInputElement>(null);
  // 项目部署URL产物
  const [artifactUrlOpen, setArtifactUrlOpen] = useState(false);
  const [artifactUrl, setArtifactUrl] = useState('');
  const [artifactUrlSaving, setArtifactUrlSaving] = useState(false);
  const [latestDeploymentUrl, setLatestDeploymentUrl] = useState<any>(null);
  const [agentFactoryOptions, setAgentFactoryOptions] = useState<AgentFactoryOptions | null>(null);
  const [agentFactoryStatus, setAgentFactoryStatus] = useState<AgentFactoryProjectStatus | null>(null);
  const [agentFactoryLoading, setAgentFactoryLoading] = useState(false);
  const agentSetupPrefilledRef = useRef('');
  const wasAgentRunningRef = useRef(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // 引用历史弹窗
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [quoteTasks, setQuoteTasks] = useState<any[]>([]);
  const [quotePage, setQuotePage] = useState(0);
  const [quoteTotal, setQuoteTotal] = useState(0);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteSearch, setQuoteSearch] = useState('');
  const searchTimer = useRef<any>(null);
  // 当前项目内所有用户提交的任务与问题原文
  const [projectPromptsOpen, setProjectPromptsOpen] = useState(false);
  const [projectPrompts, setProjectPrompts] = useState<ProjectPrompt[]>([]);
  const [projectPromptUsers, setProjectPromptUsers] = useState<ProjectPromptUser[]>([]);
  const [projectPromptStats, setProjectPromptStats] = useState<ProjectPromptStats>(EMPTY_PROJECT_PROMPT_STATS);
  const [projectPromptLoading, setProjectPromptLoading] = useState(false);
  const [projectPromptPage, setProjectPromptPage] = useState(1);
  const [projectPromptTotal, setProjectPromptTotal] = useState(0);
  const [projectPromptKeyword, setProjectPromptKeyword] = useState('');
  const [projectPromptMode, setProjectPromptMode] = useState<'all' | 'design' | 'development'>('all');
  const [projectPromptUserId, setProjectPromptUserId] = useState('');
  const [openedSessionLabel, setOpenedSessionLabel] = useState('');
  const projectPromptLoadSeqRef = useRef(0);
  // 搜索框值变化时防抖走全局搜索
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!quoteModalOpen) return;
    searchTimer.current = setTimeout(() => {
      setQuotePage(0);
      loadQuoteTasks(0, quoteSearch);
    }, 400);
  }, [quoteSearch]);
  const [previewTask, setPreviewTask] = useState<any>(null); // 小眼睛查看完整内容
  const [previewLoading, setPreviewLoading] = useState(false);
  const PAGE_SIZE = 10;
  // 引用标记
  const [quoteRef, setQuoteRef] = useState<{ preview: string; fullContent: string } | null>(null);
  const [commandPanelPinned, setCommandPanelPinned] = useState(true);
  // 右侧操作面板固定展示，去掉旧版 hover 折叠状态，避免执行中鼠标移入/移出触发整屏重排闪烁。
  const commandPanelExpanded = true;

  const addMsg = useCallback((role: string, content: string) => {
    wsRef.current.addMsg(role, content);
  }, []);

  useEffect(() => {
    if (!agentRunning) { setStuckDetected(false); return; }
    const timer = setInterval(() => { if (lastTurnTime > 0 && Date.now() - lastTurnTime > 180000) setStuckDetected(true); }, 10000);
    return () => clearInterval(timer);
  }, [agentRunning, lastTurnTime]);

  useEffect(() => {
    const token = useAuthStore.getState().token; if (!token) return;
    const socket = io('/ws', { auth: { token } }); socketRef.current = socket;
    (window as any).__ws = socket;  // 暴露给 team-collab 等页面共享
    socket.on('agent:progress', (d: any) => { if (d.sessionId !== sessionIdRef.current || (d.mode && d.mode !== wsRef.current.devMode)) return; wsRef.current.setThinkingPhase(d.phase); setLastTurnTime(Date.now()); setLastTurn(d.turn); setStuckDetected(false); });
    socket.on('agent:llm_start', (d: any) => { if (d.sessionId !== sessionIdRef.current || (d.mode && d.mode !== wsRef.current.devMode)) return; wsRef.current.setLlmModel(d.model); wsRef.current.setLlmStatus(d.model, 'calling'); setStreamingContent(''); });
    socket.on('agent:token_stream', (d: any) => { if (d.sessionId !== sessionIdRef.current || (d.mode && d.mode !== wsRef.current.devMode)) return; setStreamingContent(d.content || ''); setLastTurnTime(Date.now()); });
    socket.on('agent:assistant_text', (d: any) => { if (d.sessionId !== sessionIdRef.current || (d.mode && d.mode !== wsRef.current.devMode)) return; wsRef.current.setLlmStatus(wsRef.current.llmModel, 'done'); setStreamingContent(''); addMsg('assistant', d.content); });
    socket.on('agent:tool:start', (d: any) => { if (d.sessionId !== sessionIdRef.current || (d.mode && d.mode !== wsRef.current.devMode)) return; setStreamingContent(''); addMsg('tool', `<b>🔧 ${d.tool}</b>`); setLastTurnTime(Date.now()); setStuckDetected(false); });
    socket.on('agent:tool:done', (d: any) => { if (d.sessionId !== sessionIdRef.current || (d.mode && d.mode !== wsRef.current.devMode)) return; addMsg('tool', `✅ ${(d.output||'').slice(0,200)}`); });
    socket.on('agent:done', (d: any) => { if (d.sessionId !== sessionIdRef.current || (d.mode && d.mode !== wsRef.current.devMode)) return; wsRef.current.setAgentRunning(false); wsRef.current.setElapsedMs(d.elapsedMs||0); setStreamingContent(''); addMsg('assistant', d.summary||''); });
    socket.on('agent:error', (d: any) => { if (d.sessionId !== sessionIdRef.current || (d.mode && d.mode !== wsRef.current.devMode)) return; wsRef.current.setAgentRunning(false); setStreamingContent(''); addMsg('assistant', `❌ ${d.error}`); });
    socket.on('agent:interrupted', (d: any) => { if (d.sessionId !== sessionIdRef.current || (d.mode && d.mode !== wsRef.current.devMode)) return; wsRef.current.setAgentRunning(false); wsRef.current.setElapsedMs(d.elapsedMs||0); setStreamingContent(''); wsRef.current.setInterruptedSession({ id: d.sessionId, summary: d.summary, turns: d.turns, elapsedMs: d.elapsedMs, todos: d.todos, maxTurns: d.maxTurns }); addMsg('assistant', d.summary||''); });
    socket.on('agent:resumed', (d: any) => { if (d.sessionId !== sessionIdRef.current || (d.mode && d.mode !== wsRef.current.devMode)) return; wsRef.current.setInterruptedSession(null); wsRef.current.setAgentRunning(true); addMsg('assistant', d.message||'🔄 继续执行...'); setLastTurnTime(Date.now()); setStuckDetected(false); });
    socket.on('agent:turn_warning', (d: any) => { if (d.sessionId !== sessionIdRef.current || (d.mode && d.mode !== wsRef.current.devMode)) return; addMsg('assistant', `⚠️ 第${d.turn}轮超时，自动重试中...`); setLastTurnTime(Date.now()); });
    socket.on('agent:todo', (d: any) => { if (d.sessionId !== sessionIdRef.current || (d.mode && d.mode !== wsRef.current.devMode)) return; wsRef.current.setTodos(d.todos||[]); setNewTaskStarted(false); });
    socket.on('agent:token', (d: any) => { if (d.sessionId !== sessionIdRef.current || (d.mode && d.mode !== wsRef.current.devMode)) return; wsRef.current.addTokens(d.total); wsRef.current.setTotalTurns(d.turn); });
    // 重连后检测：只跑一次且加500ms防抖，避免频繁重连导致整页抖动
    let reconnectChecked = false;
    let reconnectTimer: any = null;
    socket.on('connect', () => {
      if (reconnectTimer) clearTimeout(reconnectTimer);
      reconnectTimer = setTimeout(() => {
        if (reconnectChecked) return;
        reconnectChecked = true;
        if (wsRef.current.agentRunning && sessionIdRef.current) {
          api.get(`/dev-workspace/sessions/${sessionIdRef.current}`).then(r => {
            if (!r.data) {
              wsRef.current.setAgentRunning(false);
              addMsg('assistant', '⚠️ 后台连接已断开，当前任务中断。');
              sessionIdRef.current = '';
            } else if (r.data.status === 'completed') {
              wsRef.current.setAgentRunning(false);
              if (r.data.summary) addMsg('assistant', r.data.summary);
            } else if (r.data.status !== 'active') {
              wsRef.current.setAgentRunning(false);
              addMsg('assistant', '⚠️ 后台服务重启，当前任务已被中断。请重新发送指令继续。');
              sessionIdRef.current = '';
            }
          }).catch(() => {
            wsRef.current.setAgentRunning(false);
            addMsg('assistant', '⚠️ 后台连接已断开，当前任务中断。');
            sessionIdRef.current = '';
          });
        }
      }, 500);
    });
    socket.on('agent:kb_search', (d: any) => { if (d.sessionId !== sessionIdRef.current) return; 
      // 跳过关键词提取阶段的通知，只处理最终搜索结果
      if (d.phase === 'keywords' || d.phase === 'keywords_done') return;
      const sources = (d.results||[]).map((r:any) => r.source).filter((v:string,i:number,a:string[]) => a.indexOf(v)===i);
      const docList = d.results
        .filter((r:any,i:number,a:any[])=>a.findIndex((x:any)=>x.source===r.source)===i)
        .slice(0,10);
      let info = d.total > 0 
        ? `<div style="font-size:13px;line-height:1.7"><b>🔍 知识库匹配到 ${d.total} 条内容</b>，引用文档（${sources.length}个）：</div><div style="margin:6px 0;max-height:150px;overflow-y:auto">${docList.map((r:any,i:number) => `<div style="display:flex;justify-content:space-between;align-items:center;padding:3px 0;border-bottom:1px solid #f0f0f0"><span style="font-size:12px;color:#555;flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;min-width:0">${i+1}. ${r.source}</span><span style="font-size:11px;color:#52c41a;background:#f6ffed;border:1px solid #b7eb8f;border-radius:4px;padding:0 6px;line-height:18px;flex-shrink:0;margin-left:8px">${((r.score||0)*100).toFixed(0)}%</span></div>`).join('')}</div>`
        : '🔍 <b>知识库搜索完成</b>，未找到高度相关的内容，将基于专业知识回答。';
      // 经验知识库匹配
      if (d.experience && d.experience.length > 0) {
        info += `<div style="margin-top:8px;font-size:13px;line-height:1.7"><b>🧠 经验知识库匹配到 ${d.experienceCount||d.experience.length} 条历史经验：</b></div><div style="margin:4px 0;max-height:150px;overflow-y:auto">`;
        d.experience.forEach((e:any,i:number) => { const tag = e.source==='team_collab'?'团队协作':'AI全流程'; info += `<div style="display:flex;justify-content:space-between;align-items:center;padding:3px 0;border-bottom:1px solid #f0f0f0"><div style="flex:1;min-width:0"><span style="font-size:11px;color:#722ed1;font-weight:500">🧠 经验</span><span style="font-size:12px;color:#888;display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${e.question?.slice(0,100)||''}</span></div><span style="font-size:11px;color:#722ed1;background:#f9f0ff;border:1px solid #d3adf7;border-radius:4px;padding:0 6px;line-height:18px;flex-shrink:0;margin-left:8px">${(e.score*100).toFixed(0)}%</span></div>`; });
        info += `</div>`;
      }
      addMsg('assistant', info);
    });
    socket.on('agent:message:queued', (d: any) => { if (d.sessionId !== sessionIdRef.current) return; addMsg('assistant', `📥 ${d.message}`); });
    socket.on('agent:message:processing', (d: any) => { if (d.sessionId !== sessionIdRef.current) return; addMsg('user', `💬 [补充] ${d.message}`); });
    return () => { delete (window as any).__ws; socket.disconnect(); };
  }, []);

  const loadWb = async () => { try { const r = await api.get('/dev-workspace/workbenches'); const wbs = r.data || []; setWorkbenches(wbs); } catch {} };
  const loadAgentFactoryStatus = useCallback(async (projectId?: string) => {
    if (!projectId) {
      setAgentFactoryStatus(null);
      return;
    }
    setAgentFactoryLoading(true);
    try {
      const status = await getAgentFactoryProjectStatus(projectId);
      setAgentFactoryStatus(status.isBusinessAgent ? status : null);
    } catch (error: any) {
      console.error('加载智能体项目状态失败:', error.response?.status, error.response?.data?.message || error.message);
      setAgentFactoryStatus(null);
    } finally {
      setAgentFactoryLoading(false);
    }
  }, []);
  const loadProjects = async () => {
    try {
      const r = await api.get('/projects');
      const all = (r.data||[]).filter((p:any)=>!p.deletedAt);
      setProjects(all);
      if (all.length === 0 || selectedProjectId) return;
      // 优先恢复最近使用的项目，只在首屏初始化一次，避免异步回包把用户刚切换的项目覆盖回默认项目
      const requestedProjectId = new URLSearchParams(window.location.search).get('projectId');
      const lastProjectId = localStorage.getItem('devforge-last-project');
      const requestedExists = requestedProjectId && all.some((p:any) => p.id === requestedProjectId);
      const lastExists = lastProjectId && all.some((p:any) => p.id === lastProjectId);
      const defaultId = (requestedExists ? requestedProjectId : lastExists ? lastProjectId : all[0].id);
      setSelectedProjectId(defaultId);
      wsRef.current.setProjectId(defaultId);
      localStorage.setItem('devforge-last-project', defaultId);
      const mode = wsRef.current.devMode as DevMode;
      const seq = ++projectLoadSeqRef.current;
      const cached = wsRef.current.restoreModeCache(mode, defaultId);
      if (cached) { sessionIdRef.current = wsRef.current.sessionId; return; }
      const has = await loadActiveSession(mode, defaultId);
      if (projectLoadSeqRef.current !== seq) return;
      if (!has) await loadRecentHistory(mode, defaultId);
      if (projectLoadSeqRef.current === seq) wsRef.current.saveModeCache(mode, defaultId);
    } catch (e: any) {
      console.error('加载项目失败:', e.message);
    }
  };
  useEffect(() => {
    loadWb();
    loadProjects();
    getAgentFactoryOptions().then(setAgentFactoryOptions).catch(() => setAgentFactoryOptions(null));
  }, []);
  // 加载可用模型列表
  useEffect(() => {
    api.get('/llm/models-with-provider').then(r => {
      setModels(r.data || []);
      if (r.data?.length > 0 && !selectedModelId) { const saved = (() => { try { return localStorage.getItem('dev-ws-model'); } catch { return null; } })(); const found = saved ? r.data.find((m: any) => m.id === saved) : null; setSelectedModelId(found?.id || r.data[0].id); }
    }).catch(() => {});
  }, []);

  // 加载用户已安装组件
  useEffect(() => {
    if (!selectedWbId) return;
    api.get(`/tech-components/user/${selectedWbId}`).then(r => setUserComps(r.data || [])).catch(() => {});
  }, [selectedWbId]);
  // ── 项目选择后同步对应的工作台 ──
  useEffect(() => {
    if (!selectedProjectId || workbenches.length === 0) return;
    const matching = workbenches.filter(w => w.projectId === selectedProjectId);
    if (matching.length > 0) {
      setSelectedWbId(matching[0].id);
    } else if (workbenches.length > 0 && !selectedWbId) {
      // 项目没有关联工作台时，fallback 到第一个可用工作台
      setSelectedWbId(workbenches[0].id);
    }
  }, [selectedProjectId, workbenches]);
  // 加载UI风格列表（含中文名和预览URL）
  useEffect(() => { api.get('/dev-workspace/sessions/styles').then(r => { 
    const styles = (r.data?.styles||[]);
    setStyleDetail(styles);
    const opts = styles.map((s:any) => ({ label: `${s.cnName || s.name}`, value: s.name }));
    setStyleOptions([{ label: '无特殊风格', value: 'none' }, ...opts]); 
  }).catch(() => {}); }, []);
  // 页面加载时检查是否有被中断的会话
  useEffect(() => {
    const checkInterrupted = async () => {
      try {
        const r = await api.get('/dev-workspace/sessions/last-interrupted', { params: { mode: devMode, projectId: selectedProjectId || undefined, workbenchId: selectedWbId || undefined } });
        if (r.data?.session) {
          wsRef.current.setInterruptedSession(r.data.session);
        }
      } catch {}
    };
    if (selectedProjectId) checkInterrupted();
  }, [selectedProjectId, devMode]);
  useEffect(() => {
    if (!selectedProjectId) { setLatestDeploymentUrl(null); return; }
    api.get(`/projects/${selectedProjectId}/deployment-url`).then(r => {
      setLatestDeploymentUrl(r.data?.url ? r.data : null);
      if (r.data?.url) setArtifactUrl(r.data.url);
    }).catch(() => setLatestDeploymentUrl(null));
  }, [selectedProjectId]);
  useEffect(() => {
    const project = projects.find(item => item.id === selectedProjectId);
    if (project?.settings?.projectKind !== 'business_agent') {
      setAgentFactoryStatus(null);
      return;
    }
    loadAgentFactoryStatus(selectedProjectId);
  }, [selectedProjectId, projects, loadAgentFactoryStatus]);
  useEffect(() => {
    const setupRequested = new URLSearchParams(window.location.search).get('agentSetup') === '1';
    if (
      !setupRequested ||
      !agentFactoryStatus?.isBusinessAgent ||
      !selectedWbId ||
      messages.length > 0 ||
      agentRunning ||
      agentSetupPrefilledRef.current === selectedProjectId
    ) return;
    const initialRequirement = agentFactoryStatus.businessGoal?.trim();
    if (initialRequirement) {
      setLocalInput(initialRequirement);
      wsRef.current.setChatInput(initialRequirement);
    }
    agentSetupPrefilledRef.current = selectedProjectId;
    const url = new URL(window.location.href);
    url.searchParams.delete('agentSetup');
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  }, [agentFactoryStatus, selectedProjectId, selectedWbId, messages.length, agentRunning]);
  useEffect(() => {
    if (wasAgentRunningRef.current && !agentRunning && agentFactoryStatus?.isBusinessAgent) {
      loadAgentFactoryStatus(selectedProjectId);
    }
    wasAgentRunningRef.current = agentRunning;
  }, [agentRunning, agentFactoryStatus?.isBusinessAgent, selectedProjectId, loadAgentFactoryStatus]);
  useEffect(() => {
    if (!messages.length) return;
    if (prependingHistoryRef.current) { prependingHistoryRef.current = false; return; }
    const raf = requestAnimationFrame(() => chatEndRef.current?.scrollIntoView({ block: 'end', behavior: agentRunning ? 'auto' : 'smooth' }));
    return () => cancelAnimationFrame(raf);
  }, [messages.length, agentRunning]);

  // 任务队列：agentRunning变为false且有排队任务时，自动执行下一个
  // 但如果needConfirm=true（被中断/需确认），不自动执行
  const taskQueueRef = useRef(taskQueue);
  taskQueueRef.current = taskQueue;
  const devModeRef = useRef(devMode);
  devModeRef.current = devMode;
  useEffect(() => {
    if (!agentRunning && !wsRef.current.needConfirm && taskQueueRef.current.length > 0) {
      const next = taskQueueRef.current[0];
      // 先移出队列再启动定时器，避免setTaskQueue触发cleanup清掉timer
      setTaskQueue(prev => prev.slice(1));
      const timer = setTimeout(() => {
        addMsg('user', next.message);
        setSending(true); wsRef.current.setAgentRunning(true);
        wsRef.current.setTodos([]); wsRef.current.setTotalTokens(0); wsRef.current.setTotalTurns(0); wsRef.current.setElapsedMs(0);
        setLastTurnTime(Date.now()); setStuckDetected(false);
        api.post('/dev-workspace/sessions', { workbenchId: selectedWbId, message: next.message, mode: next.mode || devModeRef.current, projectId: selectedProjectId, kbSearchDepth: (devModeRef.current === 'design' && useKb) ? kbDepth : 0, designOutputType: designOutput, webStyle })
          .then(r => { wsRef.current.setSessionId(r.data.sessionId); sessionIdRef.current = r.data.sessionId; })
          .catch(e => { wsRef.current.setAgentRunning(false); addMsg('assistant', `❌ ${e.response?.data?.message || e.message}`); })
          .finally(() => setSending(false));
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [agentRunning]);

  // 用 ref 保存 ws 避免 useCallback 闭包问题
  const wsRef = useRef(ws);
  wsRef.current = ws;

  // 加载当前mode的active session
  const loadActiveSession = async (mode: DevMode, projectId?: string) => {
    try {
      const r = await api.get('/dev-workspace/sessions/active', { params: { mode, projectId: projectId || undefined } });
      const s = r.data?.session;
      if (!s || s.status !== 'active') return false;
      wsRef.current.setSessionId(s.id); sessionIdRef.current = s.id;
      wsRef.current.setTotalTurns(s.totalTurns||0); wsRef.current.setTotalTokens(s.totalTokens||0); wsRef.current.setElapsedMs(s.elapsedMs||0);
      wsRef.current.setAgentRunning(true);
      if (s.todos) wsRef.current.setTodos(s.todos);
      // 恢复LLM状态，让跑马灯显示正在调用的模型名
      if (s.llmModel) wsRef.current.setLlmModel(s.llmModel);
      if (s.llmModel) wsRef.current.setLlmStatus(s.llmModel, 'calling');
      if (s.messages?.length) wsRef.current.setMessages(s.messages.map((m:any)=>({id:m.id,role:m.role,content:m.role==='tool'?`<b>🔧 ${m.toolName||''}</b><br/>${(m.content||'').slice(0,120)}`:m.content})));
      return true;
    } catch { return false; }
  };

  const buildLightHistoryMessages = (session: any, detail: any, label = '最近一次任务') => {
    const historyMessages: any[] = [];
    const firstUser = (detail.messages || []).find((m: any) => m.role === 'user')?.content || detail.userMessage || session.userMessage;
    if (firstUser) {
      historyMessages.push({
        id: `history-user-${session.id}`,
        role: 'user',
        content: `📌 ${label}\n\n${String(firstUser)}`,
      });
    }
    const summaryParts: string[] = [];
    if (detail.summary) summaryParts.push(String(detail.summary));
    else {
      const lastAssistant = [...(detail.messages || [])].reverse().find((m: any) => m.role === 'assistant')?.content;
      if (lastAssistant) summaryParts.push(String(lastAssistant));
    }
    if (detail.todos?.length) {
      const doneCount = detail.todos.filter((t:any) => t.status === 'completed').length;
      const totalCount = detail.todos.length;
      summaryParts.push(`📋 任务进度: ${doneCount}/${totalCount}`);
    }
    if (detail.elapsedMs) {
      const mins = Math.round(detail.elapsedMs / 60000);
      summaryParts.push(`⏱️ 耗时: ${mins}分钟`);
    }
    if (summaryParts.length > 0) {
      historyMessages.push({
        id: `history-summary-${session.id}`,
        role: 'assistant',
        content: `✅ ${label === '最近一次任务' ? '最近任务结果' : '历史任务结果'}\n\n${summaryParts.join('\n')}`,
      });
    }
    return historyMessages;
  };

  // 加载当前mode的最近一条历史
  const loadRecentHistory = async (mode: DevMode, projectId?: string) => {
    historySkipRef.current = 0;
    hasOlderHistoryRef.current = false;
    autoHistoryFillKeyRef.current = '';
    try {
      const r = await api.get('/dev-workspace/sessions', { params: { mode, projectId: projectId || undefined, skip: '0', take: '1' } });
      const sessions = (r.data||[]).filter((s:any)=>s.status!=='active');
      if (sessions.length > 0) {
        const latest = sessions[0];
        const detail = await api.get(`/dev-workspace/sessions/${latest.id}`);
        const d = detail.data;
        const historyMessages = buildLightHistoryMessages(latest, d, '最近一次任务');
        wsRef.current.setMessages(historyMessages);
        historySkipRef.current = 1;
        hasOlderHistoryRef.current = true;
        wsRef.current.setSessionId(latest.id); sessionIdRef.current = latest.id;
        wsRef.current.setTotalTurns(latest.totalTurns||0); wsRef.current.setTotalTokens(latest.totalTokens||0); wsRef.current.setElapsedMs(latest.elapsedMs||0);
        if (latest.todos) wsRef.current.setTodos(latest.todos);
      }
    } catch {}
  };

  const loadOlderHistory = async () => {
    if (loadingOlderHistoryRef.current || !hasOlderHistoryRef.current || agentRunning) return;
    loadingOlderHistoryRef.current = true;
    const el = chatScrollRef.current;
    const previousHeight = el?.scrollHeight || 0;
    try {
      const skip = historySkipRef.current;
      const r = await api.get('/dev-workspace/sessions', { params: { mode: wsRef.current.devMode, projectId: selectedProjectId || undefined, skip: String(skip), take: '1' } });
      const allReturned = r.data || [];
      const sessions = allReturned.filter((s:any)=>s.status!=='active');
      if (allReturned.length === 0) { hasOlderHistoryRef.current = false; return; }
      historySkipRef.current = skip + 1;
      if (sessions.length === 0) return; // 当前skip处是active会话，已跳过，等下次滚动继续
      const older = sessions[0];
      const detail = await api.get(`/dev-workspace/sessions/${older.id}`);
      const olderMessages = buildLightHistoryMessages(older, detail.data, '更早一条历史任务');
      if (olderMessages.length === 0) return;
      prependingHistoryRef.current = true;
      wsRef.current.setMessages([...olderMessages, ...wsRef.current.messages]);
      requestAnimationFrame(() => {
        const node = chatScrollRef.current;
        if (node) node.scrollTop = Math.max(0, node.scrollHeight - previousHeight + 8);
      });
    } catch {
    } finally {
      loadingOlderHistoryRef.current = false;
    }
  };

  useEffect(() => {
    const el = chatScrollRef.current;
    if (!el) return;
    const onScroll = () => {
      if (el.scrollTop < 48) loadOlderHistory();
    };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [selectedProjectId, agentRunning]);

  // 当前历史内容太少、没有滚动条时，scroll 事件不会触发；只自动补一条。
  // 之前这里会随着 messages.length 变化持续补历史，历史多的项目会出现连续重排，表现为页面闪烁、控件短暂消失。
  useEffect(() => {
    if (agentRunning || messages.length === 0 || !hasOlderHistoryRef.current) return;
    const el = chatScrollRef.current;
    if (!el) return;
    const autoFillKey = `${selectedProjectId || 'none'}:${devMode}`;
    if (autoHistoryFillKeyRef.current === autoFillKey) return;
    const timer = window.setTimeout(() => {
      const node = chatScrollRef.current;
      if (node && node.scrollHeight <= node.clientHeight + 24 && hasOlderHistoryRef.current) {
        autoHistoryFillKeyRef.current = autoFillKey;
        loadOlderHistory();
      }
    }, 80);
    return () => window.clearTimeout(timer);
  }, [messages.length, selectedProjectId, agentRunning]);

  // 历史由 loadProjects 完成后加载（避免多次触发）


  // 加载引用历史列表（search非空时全局搜索不分页）
  // ═══════ 项目记忆 CRUD ═══════
  const loadMemories = async () => {
    if (!devProjectId) return;
    try { const r = await api.get(`/dev-workspace/memories?projectId=${devProjectId}`); setMemories(r.data||[]); } catch { setMemories([]); }
  };
  const saveMemory = async () => {
    if (!memoryEdit?.title?.trim() || !memoryEdit?.content?.trim()) { message.warning('请填写标题和内容'); return; }
    const body = { projectId: devProjectId, type: memoryEdit.type||'rule', title: memoryEdit.title, content: memoryEdit.content };
    try {
      if (memoryEdit.id) { await api.patch(`/dev-workspace/memories/${memoryEdit.id}`, body); message.success('已更新'); }
      else { await api.post('/dev-workspace/memories', body); message.success('已保存'); }
      setMemoryEdit(null); loadMemories();
    } catch (e:any) { message.error(e?.response?.data?.message||'保存失败'); }
  };
  const deleteMemory = async (id: string) => {
    try { await api.delete(`/dev-workspace/memories/${id}`); message.success('已删除'); loadMemories(); } catch { message.error('删除失败'); }
  };
  const extractMemories = async () => {
    if (!selectedProjectId) { message.warning('请先选择项目'); return; }
    setMemoryExtracting(true);
    try {
      const r = await api.get('/dev-workspace/sessions', { params: { mode: devMode, projectId: selectedProjectId, skip:'0', take:'1' } });
      const sessions = r.data || [];
      if (!sessions.length) { message.warning('该项目没有已完成的任务'); return; }
      const extractRes = await api.post('/dev-workspace/memories/extract', { sessionId: sessions[0].id, projectId: selectedProjectId });
      if (extractRes.data?.success) {
        message.success(`AI提炼完成，新增 ${extractRes.data.saved} 条记忆`);
        loadMemories();
      } else {
        message.error(extractRes.data?.message || 'AI提炼失败');
      }
    } catch (e:any) { message.error(e?.response?.data?.message||'提炼失败'); }
    finally { setMemoryExtracting(false); }
  };

  const loadProjectPrompts = async (options: {
    page?: number;
    keyword?: string;
    mode?: 'all' | 'design' | 'development';
    userId?: string;
  } = {}) => {
    if (!selectedProjectId) return;
    const nextPage = options.page ?? projectPromptPage;
    const nextKeyword = options.keyword ?? projectPromptKeyword;
    const nextMode = options.mode ?? projectPromptMode;
    const nextUserId = options.userId ?? projectPromptUserId;
    const loadSeq = ++projectPromptLoadSeqRef.current;
    setProjectPromptLoading(true);
    try {
      const r = await api.get('/dev-workspace/sessions/project-prompts', {
        params: {
          projectId: selectedProjectId,
          page: nextPage,
          pageSize: PROJECT_PROMPT_PAGE_SIZE,
          keyword: nextKeyword.trim() || undefined,
          mode: nextMode === 'all' ? undefined : nextMode,
          userId: nextUserId || undefined,
        },
      });
      if (loadSeq !== projectPromptLoadSeqRef.current) return;
      setProjectPrompts(r.data?.items || []);
      setProjectPromptUsers(r.data?.users || []);
      setProjectPromptStats(r.data?.stats || EMPTY_PROJECT_PROMPT_STATS);
      setProjectPromptTotal(r.data?.total || 0);
      setProjectPromptPage(nextPage);
    } catch (e: any) {
      if (loadSeq !== projectPromptLoadSeqRef.current) return;
      setProjectPrompts([]);
      setProjectPromptTotal(0);
      message.error(e?.response?.data?.message || '任务与问题原文加载失败');
    } finally {
      if (loadSeq === projectPromptLoadSeqRef.current) setProjectPromptLoading(false);
    }
  };

  const openProjectPrompts = () => {
    if (!selectedProjectId) {
      message.warning('请先选择项目');
      return;
    }
    setProjectPromptsOpen(true);
    setProjectPrompts([]);
    setProjectPromptUsers([]);
    setProjectPromptStats(EMPTY_PROJECT_PROMPT_STATS);
    setProjectPromptTotal(0);
    setProjectPromptPage(1);
    setProjectPromptKeyword('');
    setProjectPromptMode('all');
    setProjectPromptUserId('');
    void loadProjectPrompts({ page: 1, keyword: '', mode: 'all', userId: '' });
  };

  const normalizeDevMode = (mode?: string): DevMode => {
    if (mode === 'design') return 'design';
    if (mode === 'development' || mode === 'develop') return 'development';
    return wsRef.current.devMode as DevMode;
  };

  const formatSessionMessages = (detail: any) => {
    const sessionMessages = Array.isArray(detail?.messages) ? detail.messages : [];
    if (sessionMessages.length > 0) {
      return sessionMessages.map((m: any, index: number) => ({
        id: m.id || `${detail.id || 'session'}-${index}-${m.createdAt || ''}`,
        role: m.role,
        content: m.role === 'tool'
          ? `<b>🔧 ${m.toolName || ''}</b><br/>${String(m.content || '').slice(0, 5000)}`
          : m.content,
      }));
    }
    return buildLightHistoryMessages(detail, detail, '历史任务');
  };

  const openSessionRecord = async (target: { sessionId?: string; id?: string; content?: string; mode?: string; createdAt?: string }) => {
    const targetSessionId = target.sessionId || target.id;
    if (!targetSessionId) return;
    if (agentRunning && sessionIdRef.current !== targetSessionId) {
      message.warning('当前有任务正在执行，完成或停止后再打开其他会话记录');
      return;
    }

    setProjectPromptLoading(true);
    try {
      const detailRes = await api.get(`/dev-workspace/sessions/${targetSessionId}`);
      const detail = detailRes.data || {};
      const targetMode = normalizeDevMode(detail.mode || target.mode);
      wsRef.current.saveModeCache(wsRef.current.devMode, selectedProjectId);
      wsRef.current.setDevMode(targetMode);
      wsRef.current.setMessages(formatSessionMessages(detail));
      wsRef.current.setTodos(Array.isArray(detail.todos) ? detail.todos : []);
      wsRef.current.setTotalTokens(detail.totalTokens || 0);
      wsRef.current.setTotalTurns(detail.totalTurns || 0);
      wsRef.current.setElapsedMs(detail.elapsedMs || 0);
      wsRef.current.setAgentRunning(detail.status === 'active');
      wsRef.current.setLlmModel(detail.llmModel || '');
      wsRef.current.setLlmStatus(detail.llmModel || '', detail.status === 'active' ? 'calling' : 'idle');
      wsRef.current.setInterruptedSession(null);
      wsRef.current.setNeedConfirm(false);
      wsRef.current.setSessionId(targetSessionId);
      sessionIdRef.current = targetSessionId;
      historySkipRef.current = 0;
      hasOlderHistoryRef.current = false;
      autoHistoryFillKeyRef.current = '';
      setStreamingContent('');
      setOpenedSessionLabel(`${new Date(detail.createdAt || target.createdAt || Date.now()).toLocaleString('zh-CN')} · ${targetMode === 'design' ? '设计' : '开发'}会话`);
      setProjectPromptsOpen(false);
      setHistoryOpen(false);
      setQuoteModalOpen(false);
      setPreviewTask(null);
      requestAnimationFrame(() => {
        const node = chatScrollRef.current;
        if (node) node.scrollTop = 0;
      });
      message.success('已打开当时的会话记录');
    } catch (e: any) {
      message.error(e?.response?.data?.message || '会话记录加载失败');
    } finally {
      setProjectPromptLoading(false);
    }
  };

  // 加载引用历史列表（search非空时全局搜索不分页）
  const loadQuoteTasks = async (page: number, search = '') => {
    setQuoteLoading(true);
    try {
      const params: any = { mode: devMode, projectId: selectedProjectId || undefined };
      if (search) {
        params.search = search;
        params.take = '100'; // 搜索结果一次多取点
      } else {
        params.skip = String(page * PAGE_SIZE);
        params.take = String(PAGE_SIZE);
      }
      const r = await api.get('/dev-workspace/sessions', { params });
      const sessions = (r.data || []).filter((s: any) => s.userMessage);
      setQuoteTasks(sessions.map((s: any) => ({
        id: s.id,
        userMessage: s.userMessage,
        createdAt: s.createdAt,
        mode: s.mode,
        status: s.status,
      })));
      if (search) {
        setQuoteTotal(sessions.length);
      } else {
        setQuoteTotal(page * PAGE_SIZE + sessions.length + (sessions.length === PAGE_SIZE ? PAGE_SIZE : 0));
      }
    } catch { setQuoteTasks([]); }
    setQuoteLoading(false);
  };

  const buildDevSessionContext = (task: any, detail?: any) => {
    const d = detail || task || {};
    const parts: string[] = [];
    parts.push('以下引用的是AI全流程中的一次历史任务完整上下文。请注意：这不是新任务本身，而是供你理解上下文、复盘决策、延续处理的历史资料。');
    parts.push('');
    parts.push('## 历史任务基本信息');
    parts.push(`- 任务ID: ${task?.id || d.id || '未知'}`);
    parts.push(`- 模式: ${(task?.mode || d.mode) === 'design' ? '设计' : (task?.mode || d.mode) === 'development' ? '开发' : (task?.mode || d.mode) || '未知'}`);
    parts.push(`- 创建时间: ${task?.createdAt ? new Date(task.createdAt).toLocaleString('zh-CN') : '未知'}`);
    if (d.elapsedMs) parts.push(`- 耗时: ${formatElapsed(d.elapsedMs)}`);
    if (d.totalTokens) parts.push(`- 消耗: ${(d.totalTokens / 1000).toFixed(1)}K tokens`);
    parts.push('');
    parts.push('## 用户提的问题');
    parts.push(task?.userMessage || d.userMessage || '（无）');
    parts.push('');
    if (d.summary) { parts.push('## AI任务摘要 / 最终结果'); parts.push(String(d.summary)); parts.push(''); }
    const msgs = Array.isArray(d.messages) ? d.messages : [];
    const assistantMsgs = msgs.filter((m:any) => m.role === 'assistant').map((m:any) => String(m.content || '')).filter(Boolean);
    const toolMsgs = msgs.filter((m:any) => m.role === 'tool').map((m:any) => `工具：${m.toolName || ''}\n${String(m.content || '')}`).filter(Boolean);
    if (assistantMsgs.length) { parts.push('## AI完整回复过程'); parts.push(assistantMsgs.join('\n\n---\n\n').slice(0, 18000)); parts.push(''); }
    if (toolMsgs.length) { parts.push('## 工具执行过程摘要'); parts.push(toolMsgs.join('\n\n---\n\n').slice(0, 12000)); parts.push(''); }
    if (d.todos?.length) {
      parts.push('## 任务清单');
      d.todos.forEach((t:any, i:number) => parts.push(`${i + 1}. [${t.status || 'unknown'}] ${t.content || ''}`));
      parts.push('');
    }
    return parts.join('\n');
  };

  const viewQuoteTask = async (item: any) => {
    setPreviewLoading(true);
    setPreviewTask(item);
    try {
      const detail = await api.get(`/dev-workspace/sessions/${item.id}`);
      setPreviewTask({ ...item, detail: detail.data, fullContext: buildDevSessionContext(item, detail.data) });
    } catch {
      setPreviewTask({ ...item, fullContext: buildDevSessionContext(item) });
    } finally {
      setPreviewLoading(false);
    }
  };

  const quoteDevHistoryTask = async (item: any) => {
    try {
      const detail = await api.get(`/dev-workspace/sessions/${item.id}`);
      const preview = item.userMessage.length > 50 ? item.userMessage.slice(0, 50) + '...' : item.userMessage;
      setQuoteRef({ preview, fullContent: buildDevSessionContext(item, detail.data) });
    } catch {
      const preview = item.userMessage.length > 50 ? item.userMessage.slice(0, 50) + '...' : item.userMessage;
      setQuoteRef({ preview, fullContent: item.userMessage });
    }
    setQuoteModalOpen(false);
    setQuoteSearch('');
  };


  // 切换项目：不走缓存，直接调API（保存当前模式缓存后切换）
  const switchProject = useCallback(async (v: string) => {
    if (!v || v === selectedProjectId) return;
    const seq = ++projectLoadSeqRef.current;
    setProjectSwitching(true);
    setOpenedSessionLabel('');
    wsRef.current.saveModeCache(wsRef.current.devMode, selectedProjectId);
    setSelectedProjectId(v);
    wsRef.current.setProjectId(v);
    localStorage.setItem('devforge-last-project', v);
    const wbs = workbenches.filter(w => w.projectId === v);
    if (wbs.length > 0) setSelectedWbId(wbs[0].id);
    else if (workbenches.length > 0) setSelectedWbId(workbenches[0].id); // fallback to first workbench
    wsRef.current.setMessages([]); wsRef.current.setTodos([]); wsRef.current.setTotalTokens(0); wsRef.current.setTotalTurns(0); wsRef.current.setElapsedMs(0);
    historySkipRef.current = 0; hasOlderHistoryRef.current = false;
    wsRef.current.setAgentRunning(false); wsRef.current.setLlmModel(''); wsRef.current.setThinkingPhase(''); setStreamingContent('');
    wsRef.current.setSessionId(null); sessionIdRef.current = null;
    const mode = wsRef.current.devMode as DevMode;
    const cached = wsRef.current.restoreModeCache(mode, v);
    if (cached) { sessionIdRef.current = wsRef.current.sessionId; setProjectSwitching(false); return; }
    const has = await loadActiveSession(mode, v);
    if (projectLoadSeqRef.current !== seq) return;
    if (!has) await loadRecentHistory(mode, v);
    if (projectLoadSeqRef.current === seq) {
      wsRef.current.saveModeCache(mode, v);
      setProjectSwitching(false);
    }
  }, [workbenches, selectedProjectId]);

  // 切换模式：同项目内走缓存，无缓存才调API
  const switchMode = useCallback(async (newMode: DevMode, confirmedTransition = false) => {
    if (newMode === devMode) return;
    if (
      newMode === 'development' &&
      agentFactoryStatus?.isBusinessAgent &&
      agentFactoryStatus.design?.status !== 'confirmed' &&
      !confirmedTransition
    ) {
      message.warning('请先在设计模式生成并确认产品方案和技术方案');
      return;
    }
    // 非管理员：测试和部署仅企业版可用
    if (!isAdmin && (newMode === 'testing' || newMode === 'deployment')) {
      setEnterpriseModalOpen(true);
      return;
    }
    setOpenedSessionLabel('');
    // 保存当前模式到缓存(带projectId)
    wsRef.current.saveModeCache(devMode, selectedProjectId);
    wsRef.current.setDevMode(newMode);
    // 切换模式时重置全局UI状态，避免上个模式的运行态污染新模式
    wsRef.current.setAgentRunning(false);
    wsRef.current.setLlmModel('');
    // 尝试从缓存恢复
    const restored = wsRef.current.restoreModeCache(newMode, selectedProjectId);
    if (restored) {
      sessionIdRef.current = wsRef.current.sessionId;
      return;
    }
    // 无缓存则调API
    wsRef.current.setMessages([]); wsRef.current.setTodos([]); wsRef.current.setTotalTokens(0); wsRef.current.setTotalTurns(0); wsRef.current.setElapsedMs(0);
    historySkipRef.current = 0; hasOlderHistoryRef.current = false;
    wsRef.current.setSessionId(null); sessionIdRef.current = null;
    setShowCodeTree(false);
    const hasActive = await loadActiveSession(newMode, selectedProjectId);
    if (!hasActive) { await loadRecentHistory(newMode, selectedProjectId); }
    // 新加载的数据也存入缓存
    wsRef.current.saveModeCache(newMode, selectedProjectId);
  }, [devMode, selectedProjectId, agentFactoryStatus]);

  const loadHistory = async () => { setHistoryLoading(true); try { const r = await api.get('/dev-workspace/sessions', { params: { projectId: selectedProjectId || undefined } }); setHistorySessions((r.data||[]).filter((s:any)=>s.status!=='active')); } catch{} setHistoryLoading(false); };
  const deleteSession = async (id:string) => { try{await api.delete(`/dev-workspace/sessions/${id}`);loadHistory();}catch{} };
  const clearAll = async () => { try{await api.delete('/dev-workspace/sessions');setHistorySessions([]);}catch{} };
  const copyToDialog = (msg:string) => { setLocalInput(msg); wsRef.current.setChatInput(msg); setHistoryOpen(false); };
  const replay = async (id:string) => { if(agentRunning)return; setHistoryOpen(false); wsRef.current.setMessages([]);wsRef.current.setTodos([]);wsRef.current.setTotalTokens(0);wsRef.current.setTotalTurns(0);wsRef.current.setElapsedMs(0);setSending(true);wsRef.current.setAgentRunning(true); try{const r=await api.post(`/dev-workspace/sessions/${id}/replay`);wsRef.current.setSessionId(r.data.sessionId);sessionIdRef.current=r.data.sessionId;addMsg('user',r.data.originalMessage||'');addMsg('assistant','🔄 重新执行...');}catch(e:any){wsRef.current.setAgentRunning(false);message.error(e.response?.data?.message||'失败');} setSending(false); };

  const selectedWb = workbenches.find(w=>w.id===selectedWbId);
  const selectedProject = projects.find(p=>p.id===selectedProjectId);
  const inProgressTodo = todos.findIndex((t:any)=>t.status==='in_progress');
  const doneTodos = todos.filter((t:any)=>t.status==='completed').length;
  const totalTodos = todos.filter((t:any)=>t.status!=='cancelled').length;
  const todoPct = totalTodos>0?Math.round(doneTodos/totalTodos*100):0;

  const handleSend = async () => {
    const msg = localInput.trim();
    const hasFile = !!attachedFile?.content;
    if (!msg && !hasFile || !selectedWb) return;
    
    const effectiveMsg = msg || (hasFile ? `请分析上传的文件: ${attachedFile!.name}` : '');
    if (!selectedProjectId) { message.warning('请先创建或选择一个项目，才能开始AI开发'); return; }

    // 如果有引用内容，拼到消息前面
    let fullMessage = effectiveMsg;
    const currentQuote = quoteRef;
    if (currentQuote) {
      const quotedLines = currentQuote.fullContent.split(String.fromCharCode(10)).map((l: string) => '> ' + l).join(String.fromCharCode(10));
      fullMessage = '> 引用上文：' + String.fromCharCode(10) + quotedLines + String.fromCharCode(10) + String.fromCharCode(10) + fullMessage;
      setQuoteRef(null); // 发送后清除引用标记
    }
    if (hasFile && attachedFile!.content) {
      const fc = attachedFile!.content;
      const totalChars = fc.length;
      if (totalChars < 50000) {
        // 小文件直接全文
        fullMessage = `以下是你需要分析的文件内容（全文）：\n\n## 📎 附件: ${attachedFile!.name} (共${totalChars.toLocaleString()}字符)\n\n${fc}\n\n---\n\n## 用户指令:\n${effectiveMsg}`;
      } else {
        // 大文件智能分片，保留开头
        const rawChunks = fc.split(/\n{2,}/).filter((c: string) => c.trim().length > 20);
        const chunks: string[] = [];
        for (const rc of rawChunks) {
          if (rc.length <= 4000) chunks.push(rc.trim());
          else {
            const subChunks = rc.match(/[^。！？\n]+[。！？]?/g) || [rc];
            let cur = '';
            for (const sc of subChunks) {
              if ((cur + sc).length > 4000 && cur) { chunks.push(cur.trim()); cur = sc; }
              else cur += sc;
            }
            if (cur.trim()) chunks.push(cur.trim());
          }
        }
        const headerCount = Math.min(3, chunks.length);
        const headers = chunks.slice(0, headerCount);
        const queryTokens = effectiveMsg.toLowerCase().split(/[\s，。；、,.!?？！（）()：:]+/).filter((w: string) => w.length >= 2);
        const scored = chunks.slice(headerCount).map((chunk: string, i: number) => {
          const cl = chunk.toLowerCase(); let s = 0;
          for (const t of queryTokens) s += (cl.match(new RegExp(t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g')) || []).length;
          return { chunk, score: s, idx: i + headerCount };
        }).sort((a: any, b: any) => b.score - a.score);
        const top = scored.slice(0, 5).sort((a: any, b: any) => a.idx - b.idx).map((c: any) => c.chunk);
        const selected = [...headers, ...top];
        const context = selected.map((c: string, i: number) => `### 片段${i + 1}\n${c}`).join('\n\n');
        fullMessage = `以下是你需要分析的文件内容（自动提取最相关片段，文档开头始终保留）：\n\n## 📎 附件: ${attachedFile!.name} (共${totalChars.toLocaleString()}字符，提取${selected.length}个片段)\n\n${context}\n\n---\n\n## 用户指令:\n${effectiveMsg}`;
      }
    }
    // ── 检测"继续完成任务"指令：如果有中断会话且输入包含关键词，自动continue ──
    const continueKeywords = ['继续完成', '继续任务', '继续执行', '继续上次', '恢复任务', '恢复上次', '接着做', '接着上次'];
    const isContinueCmd = continueKeywords.some(k => msg.includes(k));
    if (isContinueCmd && wsRef.current.interruptedSession) {
      wsRef.current.setChatInput('');
      setLocalInput('');
      addMsg('user', msg);
      handleContinue();
      return;
    }
    if (agentRunning) {
      // ── 补充模式：将消息推入正在执行的session ──
      if (sessionId) {
        try {
          await api.post(`/dev-workspace/sessions/${sessionId}/message`, { message: msg });
          addMsg('user', `[补充] ${msg}`);
          wsRef.current.setChatInput('');
      setLocalInput('');
        } catch (e: any) {
          message.error(e.response?.data?.message || '补充消息发送失败');
        }
        setSending(false);
        return;
      }
      // 没有活跃session才加入队列
      if (taskQueue.length >= 5) { message.warning('队列已满(最多5个)'); setSending(false); return; }
      setTaskQueue(prev => [...prev, { id: Date.now().toString(), mode: devMode, input: msg }]);
      addMsg('assistant', `📥 已加入任务队列（第${taskQueue.length + 1}位）`);
      wsRef.current.setChatInput('');
      setLocalInput('');
      setSending(false);
      return;
    }
    // 聊天显示只展示用户指令+文件名，不展示文件内容
    setOpenedSessionLabel('');
    const displayMsg = hasFile ? `📎 ${attachedFile!.name}\n${msg || '请分析上传的文件'}` : fullMessage;
    addMsg('user', displayMsg); wsRef.current.setChatInput(''); setLocalInput(''); setSending(true); wsRef.current.setAgentRunning(true);
    wsRef.current.setTodos([]); wsRef.current.setTotalTokens(0); wsRef.current.setTotalTurns(0); wsRef.current.setElapsedMs(0);
    setLastTurnTime(Date.now()); setStuckDetected(false);
    setAttachedFile(null);
    try { const effectiveModelId = selectedModelId || (models.length > 0 ? models[0].id : undefined);
    const r = await api.post('/dev-workspace/sessions', { workbenchId: selectedWbId, message: fullMessage, mode: devMode, projectId: selectedProjectId, kbSearchDepth: (devModeRef.current === 'design' && useKb) ? kbDepth : 0, designOutputType: designOutput, webStyle, enableThinking, modelId: effectiveModelId, componentIds: selectedCompIds }); wsRef.current.setSessionId(r.data.sessionId); sessionIdRef.current = r.data.sessionId; }
    catch (e: any) { wsRef.current.setAgentRunning(false); addMsg('assistant', `❌ ${e.response?.data?.message || e.message}`); }
    setSending(false);
  };

  const handleAgentDesignConfirmed = async () => {
    await switchMode('development', true);
    const buildInstruction = '请严格按照已确认的智能体 Blueprint 开发完整可运行版本，完成真实数据链路、Skill、证据溯源、结构化 HTML、测试和全部投产产物。';
    setLocalInput(buildInstruction);
    wsRef.current.setChatInput(buildInstruction);
  };

  const handleAgentAutoTest = async (input: AgentFactoryAutoTestInput) => {
    if (!selectedWbId || !selectedProjectId) throw new Error('请先选择项目工作台');
    if (agentRunning) throw new Error('当前任务仍在执行，请完成或停止后再启动自动测试');
    const effectiveModelId = selectedModelId || (models.length > 0 ? models[0].id : undefined);
    const displayMessage = `启动智能体自动测试：目标 ${input.generatedCount} 个衍生问题，最多 ${input.maxEvolutionRounds} 轮候选进化。`;
    addMsg('user', displayMessage);
    setSending(true);
    wsRef.current.setAgentRunning(true);
    wsRef.current.setTodos([]);
    wsRef.current.setTotalTokens(0);
    wsRef.current.setTotalTurns(0);
    wsRef.current.setElapsedMs(0);
    setLastTurnTime(Date.now());
    setStuckDetected(false);
    try {
      const response = await api.post('/dev-workspace/sessions', {
        workbenchId: selectedWbId,
        projectId: selectedProjectId,
        message: displayMessage,
        mode: 'development',
        enableThinking: true,
        modelId: effectiveModelId,
        agentFactoryAction: 'auto_test',
        agentFactoryTestConfig: input,
      });
      wsRef.current.setSessionId(response.data.sessionId);
      sessionIdRef.current = response.data.sessionId;
    } catch (error) {
      wsRef.current.setAgentRunning(false);
      throw error;
    } finally {
      setSending(false);
    }
  };

  // 自动执行队列中下一个任务
  const handleStop = async () => { if(!sessionId)return; try{await api.post(`/dev-workspace/sessions/${sessionId}/stop`);}catch{} wsRef.current.setAgentRunning(false); setStuckDetected(false); };
  const handleContinue = async () => {
    const intSession = wsRef.current.interruptedSession;
    if (!intSession) return;
    console.log('[CONTINUE] selectedModelId=', selectedModelId, 'models=', models.map(m => ({id: m.id, model: m.model})));
    setSending(true);
    try {
      await api.post(`/dev-workspace/sessions/${intSession.id}/continue`, { modelId: selectedModelId });
      wsRef.current.setSessionId(intSession.id); sessionIdRef.current = intSession.id;
      wsRef.current.setAgentRunning(true);
      wsRef.current.setInterruptedSession(null);
      setLastTurnTime(Date.now()); setStuckDetected(false);
    } catch (e: any) {
      message.error(e.response?.data?.message || '继续执行失败');
    }
    setSending(false);
  };
  const handleSkipInterrupted = () => {
    wsRef.current.setInterruptedSession(null);
    wsRef.current.setNeedConfirm(false);
  };
  const handleRetry = async () => { await handleStop(); setStuckDetected(false); wsRef.current.setAgentRunning(false);
  try { const r = await api.get(`/dev-workspace/sessions/${sessionId}`); const msg = r.data?.userMessage || ''; wsRef.current.setChatInput(msg); setLocalInput(msg); } catch { const msg = messages.filter(m=>m.role==='user')[0]?.content || ''; wsRef.current.setChatInput(msg); setLocalInput(msg); } };

  // 文件上传处理
  const handleFileUpload = async (file: File) => {
    setAttachedFile({ name: file.name, content: '', uploading: true });
    try {
      const result = await uploadCollabFile(file);
      if (result.success && result.content) {
        setAttachedFile({ name: result.fileName || file.name, content: result.content, uploading: false });
        message.success(`✅ ${file.name} 解析完成，${(result.charCount || 0).toLocaleString()} 字符已就绪`);
      } else {
        const errMsg = result.error || '文件解析失败';
        setAttachedFile({ name: file.name, content: '', uploading: false, error: errMsg });
        message.error(`文件上传失败: ${errMsg}`);
        setTimeout(() => setAttachedFile(null), 4000);
      }
    } catch (e: any) {
      setAttachedFile({ name: file.name, content: '', uploading: false, error: e.message || '网络错误' });
      message.error(`文件上传失败: ${e.message || '网络错误'}`);
      setTimeout(() => setAttachedFile(null), 4000);
    }
  };

  const removeAttachedFile = () => setAttachedFile(null);
  const handleSync = async () => { if(!sessionId)return; setSyncing(true); try{
    await api.post(`/dev-workspace/sessions/${sessionId}/sync`,{mode:devMode});
    // 同时创建版本里程碑
    if (selectedProjectId) {
      await api.post(`/projects/${selectedProjectId}/milestones`, { phase: devMode }).catch(() => {});
    }
    message.success('已同步到产物中心');}catch{} setSyncing(false); };
  const handleSaveArtifactUrl = async () => {
    const url = artifactUrl.trim();
    if (!selectedProjectId) { message.warning('请先选择项目'); return; }
    if (!url) { message.warning('请输入项目URL地址'); return; }
    setArtifactUrlSaving(true);
    try {
      const r = await api.post(`/projects/${selectedProjectId}/deployment-url`, { url, title: '项目访问入口' });
      const savedUrl = r.data?.artifacts?.deploymentUrl || r.data?.artifacts?.url || url;
      setLatestDeploymentUrl({ url: savedUrl, title: '项目访问入口', version: r.data?.version, updatedAt: r.data?.createdAt });
      setArtifactUrl(savedUrl);
      setArtifactUrlOpen(false);
      message.success('项目URL已同步到产物中心部署阶段');
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || '保存失败');
    }
    setArtifactUrlSaving(false);
  };
  const handleSaveWb = async (v:any) => { setSavingWb(true); try{if(editingWb) await api.put(`/dev-workspace/workbenches/${editingWb.id}`,v); else await api.post('/dev-workspace/workbenches',v); setModalOpen(false);setEditingWb(null);form.resetFields();loadWb();}catch(e:any){ message.error(e?.response?.data?.message||e?.message||'保存失败'); } setSavingWb(false); };
  const handleDelWb = async (id:string) => { try{await api.delete(`/dev-workspace/workbenches/${id}`);if(selectedWbId===id)setSelectedWbId('');loadWb();}catch{} };
  const handleTest = async (id:string) => { setTestingId(id); try{await api.post(`/dev-workspace/workbenches/${id}/test`);}catch{} setTestingId(null);loadWb(); };
  // _openCreate and _openEdit removed - unused dead code
  const loadTree = async (openModal = true) => {
    const wb = workbenches.find(w=>w.id===selectedWbId); 
    // fallback: 共享项目没有自己的workbench时，通过project获取
    if (!wb && selectedProjectId) {
      const proj = projects.find(p=>p.id===selectedProjectId);
      if (proj?.workspacePath) {
        setTreeLoading(true);
        try { const r = await api.post('/dev-workspace/sessions/file-tree', { path: proj.workspacePath }); setFileTree(formatTree(r.data||[])); if (openModal) setShowCodeTree(true); } catch(e: any) { message.error('文件树加载失败: ' + (e.response?.data?.message || e.message)); }
        setTreeLoading(false);
        return;
      }
    }
    if(!wb) { message.warning('请先选择工作台或项目'); return; }
    setTreeLoading(true); 
    try { const r = await api.post('/dev-workspace/sessions/file-tree', { path: wb.workPath }); setFileTree(formatTree(r.data||[])); if (openModal) setShowCodeTree(true); } catch(e: any) { message.error('文件树加载失败: ' + (e.response?.data?.message || e.message)); }
    setTreeLoading(false); 
  };
  // 当前选中的上传目标目录（默认为根目录）
  const [selectedTreeDir, setSelectedTreeDir] = useState('');
  // 文件树上传统一入口
  const treeFileInputRef = useRef<HTMLInputElement>(null);
  const [treeUploading, setTreeUploading] = useState(false);
  const handleTreeUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const wb = workbenches.find(w => w.id === selectedWbId);
    if (!wb) { message.error('请先选择工作台'); return; }
    setTreeUploading(true);
    let ok = 0, fail = 0;
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      try {
        const fd = new FormData();
        fd.append('file', f);
        fd.append('workbenchId', wb.id);
        fd.append('targetPath', selectedTreeDir); // 上传到用户选中的目录
        await api.post('/dev-workspace/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        ok++;
      } catch { fail++; }
    }
    setTreeUploading(false);
    if (fail > 0) message.warning(`上传完成: ✅ ${ok} 个, ❌ ${fail} 个失败`);
    else message.success(`✅ ${ok} 个文件上传成功`);
    loadTree(); // 刷新文件树
    if (treeFileInputRef.current) treeFileInputRef.current.value = '';
  };
  const formatTree = (files:any[]):any[] => files.map((f:any):any=>f.type==='directory'?{title:<Tooltip title={f.name}><span className="dw-tree-name">{f.name}</span></Tooltip>,key:f.path,icon:<FolderOutlined style={{color:'#faad14'}}/>,children:formatTree(f.children||[]),isLeaf:false,selectable:true}:{title:<Tooltip title={f.name}><span className="dw-tree-name">{f.name}</span></Tooltip>,key:f.path,icon:<FileOutlined style={{color:'#999'}}/>,isLeaf:true,selectable:true});
  const [fileViewerOpen, setFileViewerOpen] = useState(false);
  const [fileViewerPath, setFileViewerPath] = useState('');
  const [fileViewerContent, setFileViewerContent] = useState('');
  const [fileLoading, setFileLoading] = useState(false);
  const handleFileSelect = async (keys: any[]) => {
    if (!keys?.length) return;
    const filePath = keys[0] as string;
    setFileViewerPath(filePath); setFileViewerOpen(true); setFileLoading(true);
    try {
      const r = await api.post('/dev-workspace/sessions/file-content', { path: filePath, workbenchId: selectedWbId });
      setFileViewerContent(r.data?.content || r.data?.error || '');
    } catch { setFileViewerContent('读取失败'); }
    setFileLoading(false);
  };

  return (
    <div className="dev-workspace">
      <div className="dw-top-workbar">
        <div className="dw-top-project">
          <span className="dw-top-title">AI 全流程工作台</span>
          <Select className="dw-project-select" size="middle" value={selectedProjectId||undefined} onChange={(v)=>{switchProject(v);}} options={projects.map(p=>({value:p.id,label:<Space size={4}>📁 {p.name}</Space>}))}/>
          {selectedWb && <Tooltip title={selectedWb.workPath}><Text className="dw-workpath" type="secondary">{selectedWb.workPath}</Text></Tooltip>}
        </div>
        <div className="dw-top-modes">
          {MODES.map((m,i)=>(
            <Button key={m.key} type={devMode===m.key?'primary':'default'} onClick={()=>switchMode(m.key)} icon={m.icon} className={`dw-top-mode-btn dw-mode-${m.key} ${devMode===m.key?'is-active':'is-idle'}`}>
              <span className="dw-mode-label">{m.label}</span>
              <span className="dw-mode-desc">{m.desc}</span>
            </Button>
          ))}
        </div>
        <div className="dw-top-actions">
          <Tooltip title="按关键词搜索当前项目的会话任务，选择后打开当时的会话记录">
            <Button
              size="middle"
              className="dw-project-prompts-btn"
              icon={<SearchOutlined />}
              onClick={openProjectPrompts}
              disabled={!selectedProjectId}
            >
              搜索任务
            </Button>
          </Tooltip>
          {(devMode==='design'||devMode==='development')&&selectedWb&&(
            <Button size="middle" className="dw-file-structure-btn" icon={<FolderOpenOutlined/>} onClick={loadTree}>
              文件结构
            </Button>
          )}
          {projectSwitching && <Tag color="blue" icon={<LoadingOutlined spin/>} style={{borderRadius:10}}>切换项目</Tag>}
          {agentRunning && <Tag color="processing" icon={<LoadingOutlined spin/>} style={{borderRadius:10}}>执行中</Tag>}
        </div>
      </div>

      {agentFactoryStatus?.isBusinessAgent && (devMode === 'design' || devMode === 'development') && (
        <AgentFactoryWorkspace
          status={agentFactoryStatus}
          options={agentFactoryOptions}
          mode={devMode}
          loading={agentFactoryLoading}
          agentRunning={agentRunning}
          onRefresh={() => loadAgentFactoryStatus(selectedProjectId)}
          onConfirmed={handleAgentDesignConfirmed}
          onStartAutoTest={handleAgentAutoTest}
        />
      )}

      <div className="dw-main dw-workbench-shell">
        <section className="dw-workbench-center">
          {(agentRunning || stuckDetected) && (
        <div className="dw-marquee" style={{borderTop:`2px solid ${MODES.find(m=>m.key===devMode)?.color}`}}>
          {stuckDetected && (
            <div style={{background:'#fffbe6',border:'1px solid #ffe58f',borderRadius:8,padding:'10px 16px',marginBottom:8,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <span style={{color:'#d48806',fontSize:13,fontWeight:500}}>⏳ 后台仍在执行（{Math.floor((Date.now()-lastTurnTime)/1000)}秒无新消息），请耐心等待</span>
              <Space>
                <Button size="middle" danger onClick={async () => { await handleStop(); handleRetry(); }}>停止并重试</Button>
              </Space>
            </div>
          )}
          {agentRunning && !newTaskStarted && todos.length > 0 && (
            <><div className="dw-marquee-inner"><Progress percent={todoPct} size="small" status="active" strokeColor={{from:'#1677ff',to:'#722ed1'}} style={{flex:1,marginRight:16}}/><div className="dw-marquee-stats"><Badge count={`${doneTodos}/${totalTodos}`} style={{backgroundColor:'#1677ff'}}/><span style={{marginLeft:12,fontSize:12,color:'#1677ff',fontWeight:600}}>{inProgressTodo>=0?<span className="dw-marquee-current">🔄 {todos[inProgressTodo]?.content}</span>:'准备中...'}</span></div><Button size="small" danger onClick={handleStop}>停止</Button></div>
            <div className="dw-marquee-mini-steps" style={{cursor:'pointer'}} onClick={(e)=>{e.stopPropagation();setTodoListOpen(!todoListOpen);}}>{todos.filter((t:any)=>t.status!=='cancelled').map((t:any)=>(<span key={t.id} className={`dw-mini-step ${t.status}`}>{t.status==='completed'?'✅':t.status==='in_progress'?'🔵':'○'}</span>))}{totalTodos>0&&<span style={{marginLeft:6,fontSize:11,color:'#1677ff',cursor:'pointer'}}>📋</span>}</div></>
          )}
          {agentRunning && (newTaskStarted || !todos.length) && (
            <div style={{display:'flex',alignItems:'center',gap:8,padding:'6px 0'}}><LoadingOutlined spin style={{color:'#1677ff'}}/><span style={{fontSize:13,color:'#1677ff'}}>{thinkingPhase || 'AI正在分析任务...'}</span><Progress percent={50} size="small" status="active" strokeColor={{from:'#1677ff',to:'#722ed1'}} style={{flex:1}} showInfo={false}/><Button size="small" danger onClick={handleStop}>停止</Button></div>
          )}
        </div>
      )}

      {/* 子任务清单弹出面板 */}
      {todoListOpen && todos.length > 0 && (
        <div className="dw-todo-list-panel" style={{borderRadius:8,padding:'12px 16px',marginBottom:8,maxHeight:300,overflowY:'auto'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
            <Text strong style={{fontSize:13}}>📋 子任务清单 ({doneTodos}/{totalTodos})</Text>
            <Button size="small" type="text" icon={<CloseCircleOutlined/>} onClick={()=>setTodoListOpen(false)}/>
          </div>
          {todos.filter((t:any)=>t.status!=='cancelled').map((t:any,i:number)=>(
            <div key={t.id} style={{display:'flex',alignItems:'center',gap:8,padding:'4px 0',borderBottom:i<todos.filter((t2:any)=>t2.status!=='cancelled').length-1?'1px solid #f0f0f0':'none'}}>
              <span style={{fontSize:14}}>{t.status==='completed'?'✅':t.status==='in_progress'?'🔵':'⏳'}</span>
              <Text style={{fontSize:12,color:t.status==='completed'?'#52c41a':t.status==='in_progress'?'#1677ff':'#999',flex:1}}>{t.content}</Text>
              {t.status==='in_progress'&&<Tag color="processing" style={{fontSize:10,margin:0}}>进行中</Tag>}
            </div>
          ))}
        </div>
      )}

      <div className="dw-chat" style={{border:`2px solid ${MODES.find(m=>m.key===devMode)?.color}33`,borderTop:`3px solid ${MODES.find(m=>m.key===devMode)?.color}`,borderRadius:12}}>
          {devMode==='development' && latestDeploymentUrl?.url && (
            <div className="dw-deploy-url-card">
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:12}}>
                <div style={{minWidth:0}}>
                  <div style={{fontSize:12,color:'#64748b',fontWeight:600,marginBottom:4}}>项目访问地址</div>
                  <a href={latestDeploymentUrl.url} target="_blank" rel="noreferrer" title={latestDeploymentUrl.url}>
                    <LinkOutlined /> {latestDeploymentUrl.url}
                  </a>
                </div>
                <Button size="small" type="primary" ghost onClick={() => setArtifactUrlOpen(true)}>修改</Button>
              </div>
            </div>
          )}
          {/* ── 中断任务恢复Banner ── */}
          {interruptedSession && needConfirm && !agentRunning && (
            <div style={{
              background: 'linear-gradient(135deg, #fff3cd 0%, #ffe69c 100%)',
              border: '2px solid #faad14',
              borderRadius: 10,
              padding: '14px 18px',
              margin: '8px 8px 0 8px',
              boxShadow: '0 2px 12px rgba(250,173,20,0.2)',
            }}>
              <div style={{display:'flex',alignItems:'flex-start',gap:10}}>
                <span style={{fontSize:24}}>⚠️</span>
                <div style={{flex:1}}>
                  <div style={{fontWeight:700,fontSize:14,color:'#d48806',marginBottom:6}}>
                    上次任务未完成，是否继续执行？
                  </div>
                  <div style={{fontSize:13,color:'#8c6d1f',marginBottom:4}}>
                    📋 任务: {interruptedSession.userMessage || interruptedSession.summary?.slice(0,80) || '未知任务'}
                  </div>
                  {interruptedSession.todos && (() => {
                    let todos = interruptedSession.todos;
                    if (typeof todos === 'string') { try { todos = JSON.parse(todos); } catch { todos = []; } }
                    if (!Array.isArray(todos) || todos.length === 0) return null;
                    const done = todos.filter((t:any)=>t.status==='completed').length;
                    const total = todos.filter((t:any)=>t.status!=='cancelled').length;
                    const current = todos.find((t:any)=>t.status==='in_progress');
                    return (
                      <div style={{fontSize:12,color:'#ad6800',marginBottom:4}}>
                        📊 进度: {done}/{total} 已完成
                        {current && <span> | 当前步骤: <b>{current.content}</b></span>}
                      </div>
                    );
                  })()}
                  <div style={{fontSize:11,color:'#ad6800',marginTop:2}}>
                    ⏱ 耗时: {Math.round((interruptedSession.elapsedMs||0)/1000)}秒 | 🔄 轮次: {interruptedSession.totalTurns||interruptedSession.turns||0}/{interruptedSession.maxTurns||120}
                  </div>
                </div>
              </div>
              <div style={{display:'flex',gap:10,marginTop:10,justifyContent:'flex-end'}}>
                <Button type="primary" style={{background:'#faad14',borderColor:'#faad14',fontWeight:600}} onClick={handleContinue}>
                  ▶️ 继续执行
                </Button>
                <Button onClick={() => wsRef.current.setNeedConfirm(false)}>
                  ⏭️ 跳过（新任务）
                </Button>
              </div>
            </div>
          )}
          <div className="dw-chat-messages" id="dw-chat-scroll" ref={chatScrollRef}>
            {openedSessionLabel && !agentRunning && (
              <div className="dw-opened-session-hint">
                <FileSearchOutlined />
                <span>正在查看：{openedSessionLabel}</span>
                <Button size="small" type="link" onClick={() => setOpenedSessionLabel('')}>关闭提示</Button>
              </div>
            )}
            {messages.length > 0 && hasOlderHistoryRef.current && !agentRunning && <div className="dw-history-top-hint">向上翻到顶部继续加载上一条历史</div>}
            {messages.length===0&&todos.length===0?<div className="dw-empty-state"><div className="dw-empty-icon">{MODES.find(m=>m.key===devMode)?.icon}</div><Title level={4}>{MODES.find(m=>m.key===devMode)?.label}</Title><Text type="secondary">{MODES.find(m=>m.key===devMode)?.desc}</Text></div>
            :messages.map(m=><ChatMessage key={m.id} m={m} />)}
            {agentRunning&&messages.length>0&&(<div className="dw-msg assistant"><div className="dw-msg-avatar">🤖</div><div className="dw-msg-content">{llmStatus==='calling'?(streamingContent?<div style={{fontSize:14,lineHeight:1.7,whiteSpace:'pre-wrap',wordBreak:'break-word',color:'#333'}}>{streamingContent}<span className="dw-cursor-blink" style={{display:'inline-block',width:8,height:16,background:'#1677ff',marginLeft:2,verticalAlign:'middle',animation:'dw-blink 1s step-end infinite'}}></span></div>:<div style={{display:'flex',alignItems:'center',gap:8}}><LoadingOutlined spin style={{color:'#1677ff'}}/><Text type="secondary">正在调用 {llmModel||'大模型'}...</Text></div>):<div style={{display:'flex',alignItems:'center',gap:8}}><LoadingOutlined spin style={{color:'#1677ff'}}/><Text type="secondary">{thinkingPhase||'分析中...'}</Text></div>}</div></div>)}
            <div ref={chatEndRef}/>
          </div>
        </div>
        </section>

        <aside className="dw-workbench-right">
          <div
            className={`dw-chat-input dw-command-composer ${commandPanelExpanded ? 'is-expanded' : 'is-collapsed'} ${commandPanelPinned ? 'is-pinned' : 'is-unpinned'}`}
          >
            <div className="dw-command-tools">
              <div className="dw-command-tools-head">
                <div className="dw-command-tools-title"><ThunderboltOutlined /> 智能操作面板</div>
                <Tooltip title={commandPanelPinned ? '已固定，点击可自动收起' : '已解锁，鼠标移开自动收起'}>
                  <Button
                    size="small"
                    type="text"
                    className="dw-panel-pin"
                    icon={commandPanelPinned ? <LockOutlined /> : <UnlockOutlined />}
                    onClick={() => setCommandPanelPinned(!commandPanelPinned)}
                  />
                </Tooltip>
              </div>
              {quoteRef && (
                <div className="dw-quoted-history">
                  <span className="dw-quoted-history-label">📎 已引用</span>
                  <span className="dw-quoted-history-text">{quoteRef.preview}</span>
                  <span onClick={() => setQuoteRef(null)} className="dw-quoted-history-close">✕</span>
                </div>
              )}
              {(devMode==='design' || devMode==='development') && (<>
                <div className="dw-command-tools-row dw-toolbar-flat">
                  {devMode==='design' && (<>
                    <div onClick={() => setUseKb(!useKb)} className={`dw-pill-toggle ${useKb ? 'is-on' : ''}`}>
                      🔍 知识库{useKb ? '开启' : '关闭'}
                    </div>
                    {useKb && <Select size="small" value={kbDepth} className="dw-mini-select" onChange={setKbDepth}
                      options={[
                        {label:'普通匹配 (10条)',value:10},
                        {label:'强匹配 (20条)',value:20},
                        {label:'最强匹配 (50条)',value:50},
                      ]}/>}
                    <Popover
                      trigger="click"
                      placement="topLeft"
                      overlayClassName="df-readable-popover dw-output-popover"
                      content={
                        <div className="dw-output-menu">
                          {[
                            {key:'text', label:'文本输出', desc:'直接在对话中生成结构化正文', icon:<FileTextOutlined />},
                            {key:'word', label:'Word文档', desc:'适合方案、汇报、制度类文档', icon:<FileWordOutlined />},
                            {key:'excel', label:'Excel表格', desc:'适合清单、报价、台账和统计', icon:<FileExcelOutlined />},
                            {key:'pdf', label:'PDF文档', desc:'适合定稿、打印和归档材料', icon:<FilePdfOutlined />},
                          ].map(o => (
                            <div key={o.key} className={`dw-output-option ${designOutput === o.key ? 'is-active' : ''}`} onClick={() => setDesignOutput(o.key)}>
                              <span className="dw-output-option-icon">{o.icon}</span>
                              <span><b>{o.label}</b><em>{o.desc}</em></span>
                              {designOutput === o.key && <CheckCircleFilled />}
                            </div>
                          ))}
                        </div>
                      }
                    >
                      <Button size="small" className="dw-output-trigger" icon={designOutput==='word'?<FileWordOutlined/>:designOutput==='excel'?<FileExcelOutlined/>:designOutput==='pdf'?<FilePdfOutlined/>:<FileTextOutlined/>}>
                        {designOutput==='word'?'Word文档':designOutput==='excel'?'Excel表格':designOutput==='pdf'?'PDF文档':'文本输出'} <DownOutlined />
                      </Button>
                    </Popover>
                    <Button size="small" onClick={() => setStyleGalleryOpen(true)} className="dw-glow-tool-btn">
                      {webStyle === 'none' ? '🎨 选择风格' : `🎨 ${styleOptions.find(o=>o.value===webStyle)?.label || webStyle}`}
                    </Button>
                    {webStyle !== 'none' && (<Button size="small" type="text" danger onClick={() => setWebStyle('none')} className="dw-tool-clear">✕</Button>)}
                  </>)}
                  <Button size="small" icon={<HistoryOutlined/>} onClick={() => { setQuotePage(0); loadQuoteTasks(0); setQuoteModalOpen(true); }} className="dw-glow-tool-btn">引用历史</Button>
                  <Button size="small" icon={<span>🧠</span>} onClick={() => { setMemoryPanelOpen(true); loadMemories(); }} className="dw-glow-tool-btn">项目记忆</Button>
                  <Tooltip title="上传文件参考 (Word/Excel/PDF/TXT)">
                    <Button size="small" icon={<PaperClipOutlined />} onClick={() => fileInputRef.current?.click()} disabled={agentRunning} className="dw-glow-tool-btn">上传文件</Button>
                  </Tooltip>
                  {devMode==='development' && (
                    <Tooltip title="添加项目访问URL，并同步到产物中心部署阶段">
                      <Button size="small" type={latestDeploymentUrl?.url ? 'primary' : 'default'} ghost={!!latestDeploymentUrl?.url} icon={<LinkOutlined />} onClick={() => setArtifactUrlOpen(true)} className="dw-glow-tool-btn">产物界面</Button>
                    </Tooltip>
                  )}
                  <div onClick={() => setEnableThinking(!enableThinking)} className={`dw-pill-toggle ${enableThinking ? 'is-on purple' : ''}`}>
                    {enableThinking ? '🧠 深度思考开' : '🧠 深度思考'}
                  </div>
                  <Select
                    size="small"
                    value={selectedModelId}
                    onChange={(val) => { setSelectedModelId(val); try { localStorage.setItem('dev-ws-model', val || ''); } catch {} }}
                    className="dw-model-select"
                    popupMatchSelectWidth={false}
                    options={models.map(m => ({
                      value: m.id,
                      label: `${m.provider ? m.provider + ' / ' : ''}${m.model}${m.apiUrl ? ' (' + m.apiUrl + ')' : ''}`,
                    }))}
                    placeholder="选择模型"
                  />
                  <Tooltip title="扫描第三方接口文档，生成结构化API文档">
                    <Button size="small" icon={<span>📡</span>} onClick={() => setApiDocOpen(true)} disabled={agentRunning} className="dw-glow-tool-btn">接口文档</Button>
                  </Tooltip>
                  <Popover
                    open={compPopOpen}
                    onOpenChange={v => { setCompPopOpen(v); if (v && userComps.length === 0) message.info('请先在「技术组件」页面安装组件'); }}
                    trigger="click"
                    overlayClassName="df-readable-popover"
                    content={
                      <div className="dw-component-popover" style={{ maxWidth: 320 }}>
                        {userComps.map((c: any) => (
                          <div key={c.id} style={{
                            padding: '6px 4px', borderBottom: '1px solid #f0f0f0', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: 8,
                          }}
                            onClick={() => {
                              const ref = `@组件[${c.name}]#${c.uuid || c.id}`;
                              const el = chatTextAreaRef.current?.resizableTextArea?.textArea;
                              if (el) {
                                const start = el.selectionStart ?? localInput.length;
                                const end = el.selectionEnd ?? start;
                                const before = localInput.slice(0, start);
                                const after = localInput.slice(end);
                                const spacer = before && !before.endsWith(' ') ? ' ' : '';
                                const newVal = `${before}${spacer}${ref}${after}`;
                                setLocalInput(newVal);
                                wsRef.current.setChatInput(newVal);
                                setTimeout(() => { el.setSelectionRange(start + spacer.length + ref.length, start + spacer.length + ref.length); el.focus(); }, 0);
                              } else {
                                const newVal = localInput ? `${localInput} ${ref}` : ref;
                                setLocalInput(newVal);
                                wsRef.current.setChatInput(newVal);
                              }
                              if (c.uuid && !selectedCompIds.includes(c.uuid)) {
                                setSelectedCompIds([...selectedCompIds, c.uuid]);
                              }
                              setCompPopOpen(false);
                            }}
                          >
                            <Tag color={c.type === 'tool' ? 'green' : 'blue'} style={{ fontSize: 10, margin: 0 }}>
                              {c.type === 'tool' ? '工具' : '开发'}
                            </Tag>
                            <span style={{ fontSize: 12, fontWeight: 500, flex: 1 }}>{c.name}</span>
                          </div>
                        ))}
                        <div style={{ fontSize: 10, color: '#999', marginTop: 6, textAlign: 'center' }}>
                          点击组件即可插入引用到输入框
                        </div>
                      </div>
                    }
                    title="组件库">                    
                    <Button size="small" icon={<span>🧩</span>} disabled={agentRunning} className="dw-glow-tool-btn is-component">
                      组件库{selectedCompIds.length > 0 && `(${selectedCompIds.length})`}
                    </Button>
                  </Popover>
                  <input ref={fileInputRef} type="file" accept=".txt,.doc,.docx,.xls,.xlsx,.pdf" style={{ display: 'none' }}
                    onChange={(e) => { const file = e.target.files?.[0]; if (file) handleFileUpload(file); e.target.value = ''; }}
                  />
                </div>
                {attachedFile && !attachedFile.uploading && !attachedFile.error && (
                  <div className="dw-attached-file">
                    <Text strong>📎 {attachedFile.name}</Text>
                    <CloseCircleOutlined onClick={removeAttachedFile} />
                  </div>
                )}
                {attachedFile?.uploading && (
                  <div className="dw-attached-file is-loading">
                    <LoadingOutlined />
                    <Text>正在解析: {attachedFile.name}</Text>
                  </div>
                )}
              </>)}
            </div>
            <div className="dw-command-input-pane">
              <div className="dw-send-row">
                <TextArea ref={chatTextAreaRef} value={localInput} onChange={e=>setLocalInput(e.target.value)} onBlur={()=>wsRef.current.setChatInput(localInput)} onPressEnter={e=>{if(!e.shiftKey){e.preventDefault();handleSend();}}} placeholder={agentRunning?'可补充说明...':'输入任务，Enter发送...'} autoSize={{minRows:8,maxRows:18}} disabled={!selectedWb}/>
                <VoiceInputButton value={localInput} onChange={(v)=>{setLocalInput(v); wsRef.current.setChatInput(v);}} disabled={!selectedWb} />
                <Button type="primary" icon={agentRunning?<SendOutlined/>:<RocketOutlined/>} onClick={handleSend} loading={sending} disabled={!selectedWb||!selectedProjectId||(!localInput.trim()&&!agentRunning)} className="dw-send-btn">{agentRunning?'补充':'提交任务'}</Button>
              </div>
              {(totalTokens>0||(sessionId&&!agentRunning))&&<div className="dw-command-status-row">
                {totalTokens>0&&<div className="dw-stats-row"><span className="dw-stat"><ThunderboltOutlined/> {(totalTokens/1000).toFixed(1)}K</span><span className="dw-stat"><BranchesOutlined/> {totalTurns}轮</span>{elapsedMs>0&&<span className="dw-stat"><ClockCircleOutlined/> {formatElapsed(elapsedMs)}</span>}</div>}
                {sessionId&&!agentRunning&&<Button size="small" icon={<CheckOutlined/>} onClick={handleSync} loading={syncing} type="primary" ghost>同步到产物中心</Button>}
              </div>}
            </div>
          </div>
        </aside>
      </div>

      <div className="dw-history-trigger" onMouseEnter={()=>{setQueueOpen(true);}} style={{top:'52%'}}><UnorderedListOutlined style={{fontSize:20}}/><span className="dw-history-label">队列{taskQueue.length>0&&<Badge count={taskQueue.length} size="small" style={{marginLeft:4}}/>}</span></div>
      <Modal
        title={<div className="dw-code-modal-title"><FolderOpenOutlined/><span>项目文件结构</span>{selectedWb && <em>{selectedWb.workPath}</em>}</div>}
        open={showCodeTree}
        onCancel={() => { setShowCodeTree(false); setSelectedTreeDir(''); }}
        footer={null}
        width={980}
        centered
        destroyOnClose={false}
        className="dw-code-tree-modal"
        rootClassName="dw-code-tree-modal-root"
      >
        <div className="dw-code-tree-modal-body">
          <div className="dw-code-tree-toolbar">
            <div>
              <Text strong>文件浏览</Text>
              <div className="dw-code-tree-hint">点击文件夹设置上传目标，点击文件打开预览；浮层展示不会挤压工作区。</div>
            </div>
            <Space size={8}>
              {selectedTreeDir && <Tag color="blue" className="dw-upload-target">📂 {selectedTreeDir}</Tag>}
              <Button icon={<UploadOutlined/>} loading={treeUploading} onClick={()=>treeFileInputRef.current?.click()}>
                上传文件
              </Button>
              <Button icon={<ReloadOutlined/>} onClick={loadTree} loading={treeLoading}>刷新</Button>
            </Space>
            <input type="file" ref={treeFileInputRef} multiple style={{display:'none'}} onChange={handleTreeUpload}/>
          </div>
          <div className="dw-code-tree dw-code-tree-floating">
            {treeLoading ? <div className="dw-tree-loading"><Spin/><span>正在读取项目文件结构...</span></div> : (
              fileTree.length > 0
                ? <Tree showIcon defaultExpandAll treeData={fileTree} className="dw-floating-tree"
                    onSelect={(keys, info) => {
                      const node = info.node as any;
                      if (node.isLeaf === false) {
                        const dirPath = String(node.key);
                        setSelectedTreeDir(dirPath);
                        message.info(`📂 已选择上传目录: ${dirPath}`, 2);
                      } else {
                        handleFileSelect(keys, info);
                      }
                    }}/>
                : <Empty description="目录为空" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{padding:40}}/>
            )}
          </div>
        </div>
      </Modal>
      {/* 任务队列 Drawer */}
      <Drawer title={<Space><UnorderedListOutlined/> 任务队列（最多5个）</Space>} placement="right" width={500} open={queueOpen} onClose={()=>setQueueOpen(false)}>
        {/* 添加任务表单 */}
        <Card size="small" title="📝 添加新任务" style={{marginBottom:12}}>
          <Form form={queueForm} layout="vertical" onFinish={(values:any)=>{
            if(taskQueue.length>=5){message.warning('队列已满（最多5个）');return;}
            setTaskQueue(prev=>[...prev,{id:Date.now().toString(),message:values.message,mode:values.mode||devMode,createdAt:new Date().toISOString()}]);
            queueForm.resetFields(['message']);
            setQueueMessage('');
            message.success('任务已加入队列');
          }}>
            <Form.Item name="mode" label="模式" initialValue={devMode}>
              <Select size="small" options={MODES.map(m=>({value:m.key,label:m.label}))}/>
            </Form.Item>
            <Form.Item name="message" label="任务描述" rules={[{required:true,message:'请输入任务描述'}]}>
              <div className="dw-inline-voice-input">
                <Input.TextArea rows={3} value={queueMessage} onChange={e=>{setQueueMessage(e.target.value); queueForm.setFieldValue('message', e.target.value);}} placeholder="描述你要AI做什么..."/>
                <VoiceInputButton value={queueMessage} onChange={(v)=>{setQueueMessage(v); queueForm.setFieldValue('message', v);}} size="small" />
              </div>
            </Form.Item>
            <Button type="primary" htmlType="submit" block icon={<PlusOutlined/>}>加入队列</Button>
          </Form>
        </Card>
        {/* 队列列表 */}
        {taskQueue.length===0?<Empty description="队列为空，点击上方添加任务"/>:(
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            {taskQueue.map((t:any,i:number)=>(
              <Card key={t.id} size="small" title={<Space><Tag color="blue">#{i+1}</Tag><Tag color={t.mode==='design'?'#1677ff':t.mode==='development'?'#52c41a':t.mode==='testing'?'#fa8c16':t.mode==='deployment'?'#722ed1':'#999'} style={{fontSize:10}}>{t.mode==='design'?'设计':t.mode==='development'?'开发':t.mode==='testing'?'测试':t.mode==='deployment'?'部署':t.mode||'开发'}</Tag></Space>} extra={<Space size={2}><Tooltip title="编辑"><Button size="small" type="text" icon={<EditOutlined/>} onClick={()=>{const newMsg=prompt('修改任务描述:',t.message);if(newMsg&&newMsg.trim())setTaskQueue(prev=>prev.map((q:any)=>q.id===t.id?{...q,message:newMsg.trim()}:q));}}/></Tooltip><Popconfirm title="移除？" onConfirm={()=>setTaskQueue(prev=>prev.filter((q:any)=>q.id!==t.id))}><Button size="small" type="text" danger icon={<DeleteOutlined/>}/></Popconfirm></Space>}>
                <Text style={{fontSize:13}}>{t.message}</Text>
                <div style={{fontSize:10,color:'#999',marginTop:4}}>{new Date(t.createdAt).toLocaleString('zh-CN')}</div>
              </Card>
            ))}
            <div style={{textAlign:'center',marginTop:8}}><Popconfirm title="清空全部队列任务？" onConfirm={()=>setTaskQueue([])}><Button danger type="dashed" block icon={<ClearOutlined/>}>清空队列</Button></Popconfirm></div>
          </div>
        )}
      </Drawer>
      <Modal title={<Space><FileTextOutlined/>{fileViewerPath.split('/').pop()}</Space>} open={fileViewerOpen} onCancel={()=>setFileViewerOpen(false)} footer={<Button onClick={()=>setFileViewerOpen(false)}>关闭</Button>} width={800}>
        <div style={{fontSize:11,color:'#888',marginBottom:8}}>{fileViewerPath}</div>
        {fileLoading?<Spin/>:<pre style={{background:'#1e1e2e',color:'#cdd6f4',padding:16,borderRadius:8,overflow:'auto',maxHeight:'60vh',fontSize:12,lineHeight:1.6,margin:0}}>{fileViewerContent||'(空文件)'}</pre>}
      </Modal>
      {/* 项目记忆面板 */}
      <Modal
        title="🧠 项目记忆"
        open={memoryPanelOpen}
        onCancel={() => { setMemoryPanelOpen(false); setMemoryEdit(null); }}
        footer={null}
        width={700}
      >
        <div style={{display:'flex',gap:8,marginBottom:16}}>
          <Button type="primary" size="small" onClick={() => setMemoryEdit({type:'rule'})}>新增规则</Button>
          <Button size="small" loading={memoryExtracting} onClick={extractMemories}>🤖 AI提炼当前对话</Button>
          <Button size="small" onClick={loadMemories}>刷新</Button>
        </div>
        {memoryEdit !== null && (
          <div style={{background:'#f8fafc',border:'1px solid #e2e8f0',borderRadius:12,padding:14,marginBottom:14}}>
            <Select size="small" value={memoryEdit.type||'rule'} onChange={v=>setMemoryEdit({...memoryEdit,type:v})} style={{width:120,marginBottom:8}}
              options={[{value:'rule',label:'铁律'},{value:'convention',label:'约定'},{value:'config',label:'配置'},{value:'note',label:'备注'}]} />
            <Input size="small" value={memoryEdit.title||''} onChange={e=>setMemoryEdit({...memoryEdit,title:e.target.value})} placeholder="标题" style={{marginBottom:8}} />
            <div className="dw-inline-voice-input">
              <Input.TextArea rows={4} value={memoryEdit.content||''} onChange={e=>setMemoryEdit({...memoryEdit,content:e.target.value})} placeholder="详细内容，如：打包时去 /etc_data/admin/xxx 目录执行 make build，产物放 deploy/ 下" />
              <VoiceInputButton value={memoryEdit.content||''} onChange={(v)=>setMemoryEdit({...memoryEdit,content:v})} size="small" />
            </div>
            <div style={{display:'flex',gap:8,marginTop:8}}>
              <Button size="small" type="primary" onClick={saveMemory}>{memoryEdit.id?'更新':'保存'}</Button>
              <Button size="small" onClick={()=>setMemoryEdit(null)}>取消</Button>
            </div>
          </div>
        )}
        {memories.length===0 ? <Empty description="该项目暂无记忆，新增一条规则或让AI提炼当前对话" /> :
          memories.map(m=>(
            <div key={m.id} style={{display:'grid',gridTemplateColumns:'auto minmax(0,1fr) auto',gap:10,alignItems:'start',padding:'10px 0',borderBottom:'1px solid #f1f5f9'}}>
              <Tag color={m.type==='rule'?'red':m.type==='convention'?'blue':m.type==='config'?'green':'default'}>{m.type==='rule'?'铁律':m.type==='convention'?'约定':m.type==='config'?'配置':'备注'}</Tag>
              <div style={{minWidth:0}}>
                <strong style={{color:'#0f172a'}}>{m.title}</strong>
                <p style={{margin:'4px 0 0',color:'#64748b',fontSize:13,lineHeight:1.5,wordBreak:'break-word'}}>{m.content.slice(0,300)}{m.content.length>300?'...':''}</p>
              </div>
              <div style={{display:'flex',gap:4}}>
                <Button size="small" type="link" onClick={()=>setMemoryEdit(m)}>编辑</Button>
                <Popconfirm title="删除这条记忆？" onConfirm={()=>deleteMemory(m.id)}><Button size="small" type="link" danger>删除</Button></Popconfirm>
              </div>
            </div>
          ))
        }
      </Modal>

      {/* 风格画廊选择弹窗 */}
      <Modal
        title={<Space>🎨 Web设计风格画廊 <Tag color="blue">{styleDetail.length} 种风格</Tag></Space>}
        open={styleGalleryOpen}
        onCancel={() => { setStyleGalleryOpen(false); setFullPreviewStyle(null); }}
        footer={null}
        width={fullPreviewStyle ? 1100 : 1050}
        style={{ top: 20 }}
        styles={{ body: { maxHeight: fullPreviewStyle ? '85vh' : '75vh', overflowY: 'auto', padding: '12px 16px' } }}
      >
        {fullPreviewStyle && (
          <div style={{ position: 'relative', marginBottom: 16 }}>
            <Button size="small" onClick={() => setFullPreviewStyle(null)} style={{ marginBottom: 8 }}>← 返回画廊</Button>
            <div style={{ height: 'calc(80vh - 120px)', borderRadius: 8, border: '1px solid #e8e8e8', overflow: 'auto' }}>
              <iframe src={fullPreviewStyle} style={{ width: '100%', minHeight: '900px', border: 'none', display: 'block' }} />
            </div>
          </div>
        )}
        {!fullPreviewStyle && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
            {styleDetail.map((s: any) => {
              const isSelected = webStyle === s.name;
              return (
                <div key={s.name}
                  onClick={() => { setWebStyle(s.name); setStyleGalleryOpen(false); }}
                  style={{
                    border: isSelected ? '2px solid #1677ff' : '1px solid #e8e8e8',
                    borderRadius: 10, overflow: 'hidden', cursor: 'pointer',
                    background: '#fff', transition: 'all 0.2s',
                    boxShadow: isSelected ? '0 0 0 3px rgba(22,119,255,0.15)' : '0 1px 4px rgba(0,0,0,0.06)',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = isSelected ? '0 0 0 3px rgba(22,119,255,0.15)' : '0 1px 4px rgba(0,0,0,0.06)'; }}
                >
                  {/* 预览iframe */}
                  <div style={{ position: 'relative', height: 160, overflow: 'hidden', background: '#fafafa' }}>
                    <iframe
                      src={s.previewUrl}
                      style={{ width: '200%', height: 320, border: 'none', transform: 'scale(0.5)', transformOrigin: '0 0', pointerEvents: 'none' }}
                      title={s.cnName}
                    />
                    <Button
                      size="small" type="text"
                      style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,0.5)', color: '#fff', fontSize: 11 }}
                      onClick={(e) => { e.stopPropagation(); setFullPreviewStyle(s.previewUrl); }}
                    >
                      🔍 放大
                    </Button>
                  </div>
                  {/* 风格信息 */}
                  <div style={{ padding: '10px 12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <Text strong style={{ fontSize: 14 }}>{s.cnName}</Text>
                      {isSelected && <Tag color="blue" style={{ margin: 0 }}>已选</Tag>}
                    </div>
                    <Text type="secondary" style={{ fontSize: 11 }}>{s.name}</Text>
                    <div style={{ marginTop: 6, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      <Tag style={{ fontSize: 10, margin: 0 }}>{s.type || '通用'}</Tag>
                      <Tag color={s.complexity === 'Low' ? 'green' : s.complexity === 'Medium' ? 'orange' : 'red'} style={{ fontSize: 10, margin: 0 }}>{s.complexity}</Tag>
                      {s.darkMode?.includes('Full') && <Tag color="purple" style={{ fontSize: 10, margin: 0 }}>亮/暗</Tag>}
                      {s.darkMode?.includes('only') && <Tag color="default" style={{ fontSize: 10, margin: 0 }}>仅暗</Tag>}
                    </div>
                    {s.bestFor && <Text type="secondary" style={{ fontSize: 10, display: 'block', marginTop: 4 }}>适合：{s.bestFor.slice(0, 60)}</Text>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Modal>
      {/* 当前项目全部用户任务与问题原文 */}
      <Modal
        title={(
          <Space size={8}>
            <FileSearchOutlined />
            <span>搜索会话任务</span>
            <Tooltip title={selectedProject?.name || '当前项目'}>
              <Tag className="dw-project-prompt-project">{selectedProject?.name || '当前项目'}</Tag>
            </Tooltip>
          </Space>
        )}
        open={projectPromptsOpen}
        onCancel={() => {
          projectPromptLoadSeqRef.current += 1;
          setProjectPromptLoading(false);
          setProjectPromptsOpen(false);
        }}
        footer={null}
        width={980}
        className="dw-project-prompts-modal"
        rootClassName="dw-project-prompts-modal-root"
        destroyOnClose
      >
        <div className="dw-project-prompt-stats" aria-label="项目提交统计">
          <div className="dw-project-prompt-stat">
            <span>项目提交</span>
            <strong>{projectPromptStats.total}</strong>
            <small>设计与开发合计</small>
          </div>
          <div className="dw-project-prompt-stat is-mine">
            <span>我的提交</span>
            <strong>{projectPromptStats.mine}</strong>
            <small>任务和补充问题</small>
          </div>
          <div className="dw-project-prompt-stat">
            <span>参与用户</span>
            <strong>{projectPromptStats.users}</strong>
            <small>实际提交人数</small>
          </div>
          <div className="dw-project-prompt-stat">
            <span>设计 / 开发</span>
            <strong>{projectPromptStats.design} / {projectPromptStats.development}</strong>
            <small>按流程统计</small>
          </div>
        </div>

        <div className="dw-project-prompt-filters">
          <Input.Search
            value={projectPromptKeyword}
            onChange={e => setProjectPromptKeyword(e.target.value)}
            onSearch={value => {
              setProjectPromptKeyword(value);
              void loadProjectPrompts({ page: 1, keyword: value });
            }}
            allowClear
            placeholder="输入关键词，搜索相关会话任务"
            enterButton="搜索"
          />
          <Segmented
            value={projectPromptMode}
            options={[
              { label: '全部流程', value: 'all' },
              { label: '设计', value: 'design' },
              { label: '开发', value: 'development' },
            ]}
            onChange={value => {
              const nextMode = value as 'all' | 'design' | 'development';
              setProjectPromptMode(nextMode);
              void loadProjectPrompts({ page: 1, mode: nextMode });
            }}
          />
          <Select
            value={projectPromptUserId}
            showSearch
            optionFilterProp="label"
            aria-label="按提交用户筛选"
            options={[
              { value: '', label: '全部用户' },
              ...projectPromptUsers.map(user => ({
                value: user.id,
                label: `${user.name || user.username || '未知用户'}${user.isMine ? '（我）' : ''} · ${user.count || 0}次`,
              })),
            ]}
            onChange={userId => {
              setProjectPromptUserId(userId);
              void loadProjectPrompts({ page: 1, userId });
            }}
          />
          <Tooltip title="刷新">
            <Button
              aria-label="刷新任务与问题原文"
              icon={<ReloadOutlined />}
              onClick={() => void loadProjectPrompts()}
            />
          </Tooltip>
        </div>

        <Spin spinning={projectPromptLoading}>
          <List
            className="dw-project-prompt-list"
            dataSource={projectPrompts}
            locale={{ emptyText: <Empty description="当前筛选条件下暂无任务或问题" /> }}
            pagination={projectPromptTotal > PROJECT_PROMPT_PAGE_SIZE ? {
              current: projectPromptPage,
              pageSize: PROJECT_PROMPT_PAGE_SIZE,
              total: projectPromptTotal,
              showSizeChanger: false,
              showTotal: total => `共 ${total} 次提交`,
              onChange: nextPage => void loadProjectPrompts({ page: nextPage }),
            } : false}
            renderItem={item => {
              const displayName = item.user?.name || item.user?.username || '未知用户';
              return (
                <List.Item key={item.id} className={item.isMine ? 'is-mine' : ''}>
                  <div className="dw-project-prompt-meta">
                    <span className="dw-project-prompt-avatar">{displayName.slice(0, 1)}</span>
                    <div className="dw-project-prompt-author">
                      <div>
                        <Text strong>{displayName}</Text>
                        {item.isMine && <Tag color="blue">我</Tag>}
                        {item.user?.username && <Text type="secondary">@{item.user.username}</Text>}
                      </div>
                      <Text type="secondary">{new Date(item.createdAt).toLocaleString('zh-CN')}</Text>
                    </div>
                    <div className="dw-project-prompt-tags">
                      <Tag color={item.mode === 'design' ? 'blue' : 'green'}>{item.mode === 'design' ? '设计' : '开发'}</Tag>
                      <Tag>{item.kind === 'task' ? '任务提交' : '补充问题'}</Tag>
                      <Tag color={item.status === 'completed' ? 'success' : item.status === 'active' ? 'processing' : 'default'}>
                        {item.status === 'completed' ? '已完成' : item.status === 'active' ? '执行中' : item.status || '未知'}
                      </Tag>
                    </div>
                  </div>
                  <Paragraph
                    className="dw-project-prompt-content"
                    copyable={{ text: item.content, tooltips: ['复制原文', '已复制'] }}
                  >
                    {item.content}
                  </Paragraph>
                  <div className="dw-project-prompt-actions">
                    <Button size="small" type="primary" icon={<EyeOutlined />} onClick={() => void openSessionRecord(item)}>
                      打开会话
                    </Button>
                    <Text type="secondary">会话ID：{item.sessionId.slice(0, 8)}</Text>
                  </div>
                </List.Item>
              );
            }}
          />
        </Spin>
      </Modal>
      {/* 引用历史弹窗 */}
      <Modal
        title="📎 引用历史任务"
        open={quoteModalOpen}
        onCancel={() => { setQuoteModalOpen(false); setQuoteSearch(''); }}
        footer={null}
        width={760}
        className="df-history-modal"
        rootClassName="df-history-modal-root"
      >
        <Input.Search
          placeholder="搜索历史任务内容..."
          allowClear
          value={quoteSearch}
          onChange={(e) => setQuoteSearch(e.target.value)}
          onSearch={(val) => { setQuotePage(0); loadQuoteTasks(0, val); }}
          style={{ marginBottom: 16 }}
          prefix={<SearchOutlined />}
        />
        <Spin spinning={quoteLoading}>
          {quoteTasks.length === 0 ? <Empty description="暂无历史任务" /> : (
            <List
              dataSource={quoteTasks.filter((t: any) => !quoteSearch || t.userMessage.includes(quoteSearch))}
              pagination={quoteSearch ? false : {
                current: quotePage + 1,
                pageSize: PAGE_SIZE,
                total: quoteTotal,
                onChange: (p) => { const page = p - 1; setQuotePage(page); loadQuoteTasks(page); },
                showTotal: (t) => `共 ${t} 条`,
                size: 'small',
              }}
              renderItem={(item: any) => (
                <List.Item
                  actions={[
                    <Button size="small" type="text" icon={<EyeOutlined />} onClick={() => viewQuoteTask(item)}>查看</Button>,
                    <Button size="small" type="primary" onClick={() => quoteDevHistoryTask(item)}>引用</Button>
                  ]}
                >
                  <List.Item.Meta
                    title={<div className="df-history-question">{item.userMessage}</div>}
                    description={<Text type="secondary" style={{fontSize:11}}>{new Date(item.createdAt).toLocaleString('zh-CN')} · {item.mode === 'design' ? '设计' : '开发'}</Text>}
                  />
                </List.Item>
              )}
            />
          )}
        </Spin>
      </Modal>
      {/* 任务详情预览弹窗 */}
      <Modal
        title="📋 任务完整内容"
        open={!!previewTask}
        onCancel={() => setPreviewTask(null)}
        footer={null}
        width={900}
        className="df-history-modal"
        rootClassName="df-history-modal-root"
      >
        {previewTask && (
          <>
            <Text type="secondary" style={{fontSize:12}}>{new Date(previewTask.createdAt).toLocaleString('zh-CN')} · {previewTask.mode === 'design' ? '设计模式' : '开发模式'}</Text>
            <div className="dw-preview-content md-content df-rich-history-content" style={{marginTop:12, padding:18, borderRadius:14, maxHeight:'68vh', overflow:'auto', fontSize:15, lineHeight:1.85}}>
              {previewLoading ? '正在加载完整任务详情...' : <ReactMarkdown remarkPlugins={[remarkGfm]}>{previewTask.fullContext || previewTask.userMessage || ''}</ReactMarkdown>}
            </div>
            <div style={{marginTop:12, textAlign:'right'}}>
              <Button size="small" type="primary" onClick={() => {
                const preview = previewTask.userMessage.length > 50 ? previewTask.userMessage.slice(0, 50) + '...' : previewTask.userMessage;
                setQuoteRef({ preview, fullContent: previewTask.fullContext || previewTask.userMessage });
                setQuoteModalOpen(false);
                setPreviewTask(null);
                setQuoteSearch('');
              }}>引用此内容</Button>
            </div>
          </>
        )}
      </Modal>
      <Modal
        title={<Space><LinkOutlined/> 添加项目产物界面</Space>}
        open={artifactUrlOpen}
        onCancel={() => setArtifactUrlOpen(false)}
        onOk={handleSaveArtifactUrl}
        confirmLoading={artifactUrlSaving}
        okText="同步到产物中心"
        cancelText="取消"
        width={560}
      >
        <Space direction="vertical" size={12} style={{width:'100%'}}>
          <Text type="secondary">输入该项目已经部署好的访问 URL。保存后会自动同步到产物中心的「部署阶段」，并在开发模式右侧醒目展示。</Text>
          <Input
            autoFocus
            size="large"
            prefix={<LinkOutlined />}
            placeholder="例如：http://47.99.82.90:5173 或 https://example.com"
            value={artifactUrl}
            onChange={e => setArtifactUrl(e.target.value)}
            onPressEnter={handleSaveArtifactUrl}
          />
          {latestDeploymentUrl?.url && <Text type="secondary" style={{fontSize:12}}>当前地址：{latestDeploymentUrl.url}</Text>}
        </Space>
      </Modal>
      {/* 接口文档灌输弹窗 */}
      <Modal
        title="📡 扫描第三方接口文档"
        open={apiDocOpen}
        onCancel={() => { setApiDocOpen(false); setApiDocFile(null); setApiDocUrl(''); setApiDocName(''); }}
        footer={[
          <Button key="cancel" onClick={() => { setApiDocOpen(false); setApiDocFile(null); setApiDocUrl(''); setApiDocName(''); }}>取消</Button>,
          <Button key="submit" type="primary" loading={apiDocLoading} disabled={!apiDocName.trim() || (!apiDocFile && !apiDocUrl.trim())}
            onClick={async () => {
              if (!apiDocName.trim()) return;
              setApiDocLoading(true);
              message.loading({ content: '正在解析接口文档...', key: 'apiDoc', duration: 0 });
              try {
                const workPath = selectedWb?.workPath || selectedProject?.workspacePath;
                let fileContent = '';
                let fileName = '';
                if (apiDocFile) {
                  fileContent = await apiDocFile.text();
                  fileName = apiDocFile.name;
                }
                // 进度提示
                message.loading({ content: apiDocFile ? '正在提取文件内容...' : '正在抓取网页内容...', key: 'apiDoc', duration: 0 });
                
                const res = await api.post('/dev-workspace/sessions/api-doc-ingest', {
                  interfaceName: apiDocName.trim(),
                  fileContent: fileContent || undefined,
                  fileName: fileName || undefined,
                  url: apiDocUrl.trim() || undefined,
                  workPath,
                  projectId: selectedProjectId,
                });
                
                message.loading({ content: 'AI 正在分析提取接口信息...', key: 'apiDoc', duration: 0 });
                
                if (res.data?.success) {
                  message.success({ content: `✅ 接口文档已生成！
📁 保存位置：${res.data.savedPath}
📊 内容长度：${res.data.content?.length || 0} 字符`, key: 'apiDoc', duration: 6 });
                  setApiDocOpen(false);
                  const apiDocNameSaved = apiDocName.trim();
                  setApiDocFile(null); setApiDocUrl(''); setApiDocName('');
                  // 自动填入输入框（同步到store）
                  const shortContent = res.data.content ? res.data.content.slice(0, 1500) : '';
                  const summary = shortContent ? `\n\n📋 解析结果摘要：\n${shortContent}` : '';
                  const defaultMsg = `学习研究接口文档「${res.data.interfaceName}」，用于后续的开发。\n\n📁 文档已保存到：\n${res.data.savedPath}${summary}`;
                  setLocalInput(defaultMsg);
                  ws.setChatInput(defaultMsg);
                } else {
                  message.error({ content: res.data?.error || '解析失败，请重试', key: 'apiDoc' });
                }
              } catch (e: any) {
                const errMsg = e.response?.data?.message || e.message || '解析失败';
                message.error({ content: `❌ ${errMsg}`, key: 'apiDoc', duration: 5 });
              } finally {
                setApiDocLoading(false);
              }
            }}
          >开始解析</Button>,
        ]}
        width={560}
        className="df-readable-modal dw-api-doc-modal"
        rootClassName="df-readable-modal-root"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <Text strong style={{ fontSize: 13 }}>接口名称 <Text type="danger">*</Text></Text>
            <Input placeholder="例如：微信支付接口、百度地图API" value={apiDocName}
              onChange={e => setApiDocName(e.target.value)} style={{ marginTop: 4 }} />
          </div>
          <div>
            <Text strong style={{ fontSize: 13 }}>接口文档来源（文件或URL二选一）</Text>
            <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
              <Button icon={<PaperClipOutlined />} onClick={() => apiDocFileRef.current?.click()}
                style={{ flex: 1, borderStyle: apiDocFile ? 'solid' : 'dashed', borderColor: apiDocFile ? '#1677ff' : '#d9d9d9', color: apiDocFile ? '#1677ff' : undefined }}>
                {apiDocFile ? `📄 ${apiDocFile.name}` : '上传文件'}
              </Button>
              <input ref={apiDocFileRef} type="file" accept=".txt,.doc,.docx,.md,.pdf,.html,.htm,.json,.xml,.yaml,.yml" style={{ display: 'none' }}
                onChange={e => { const f = e.target.files?.[0]; if (f) { setApiDocFile(f); setApiDocUrl(''); } e.target.value = ''; }} />
              <Text type="secondary" style={{ alignSelf: 'center' }}>或</Text>
              <Input placeholder="输入网站URL" value={apiDocUrl} style={{ flex: 2 }}
                onChange={e => { setApiDocUrl(e.target.value); if (e.target.value) setApiDocFile(null); }} />
            </div>
          </div>
          <div className="dw-info-panel" style={{ borderRadius: 8, padding: 12 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              💡 支持上传 Word/PDF/TXT/Markdown/JSON 文件，或输入接口文档网页地址。
              后台会自动扫描内容，用AI提取接口信息，生成结构化Markdown文档，
              保存到工作区 <Text code>development/api-docs/</Text> 目录。
            </Text>
          </div>
        </div>
      </Modal>
      {/* 企业版功能提示弹窗 */}
      <Modal
        title="企业版功能"
        open={enterpriseModalOpen}
        onCancel={() => setEnterpriseModalOpen(false)}
        footer={[<Button key="ok" type="primary" onClick={() => setEnterpriseModalOpen(false)}>我知道了</Button>]}
        width={420}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <RocketOutlined style={{ fontSize: 48, color: '#722ed1', marginBottom: 16 }} />
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: '#333' }}>当前功能为企业版功能</div>
          <div style={{ fontSize: 14, color: '#666' }}>请联系管理员开通使用</div>
        </div>
      </Modal>
    </div>
  );
}
